import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    inject,
    signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
    MatButtonToggle,
    MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
} from '@angular/material/sidenav';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService } from '../../../modules/admin/users/users.service';
import { User } from '../../../modules/admin/users/users.types';
import {
    Absence,
    AbsencesLeaveService,
} from '../../../modules/hr/absences-leave-management/absences-leave.service';
import { HolidaysService } from '../../../modules/hr/holidays-management/holidays.service';
import { Holiday } from '../../../modules/hr/holidays-management/holidays.types';
import { ExternalContractor } from '../../../modules/mng/external-contractors/external-contractor.types';
import { ExternalContractorsService } from '../../../modules/mng/external-contractors/external-contractors.service';
import { WorkType } from '../../../modules/mng/work_types/work_types.types';
import { ProjectsService } from '../../../modules/omng/projects/projects.service';
import { Project } from '../../../modules/omng/projects/projects.types';
import { ApplyItem } from '../../models/capacity.models';
import { CapacityService } from '../../services/capacity.service';
import { FilterPrefsService } from '../../services/filter-prefs.service';
import {
    PlanningPermissions,
    PlanningPermissionsService,
} from '../../services/planning-permissions.service';
import { Utils } from '../../utils';
import { FilterComponent } from '../filter/filter.component';
import { FilterField } from '../filter/filter.types';
import { Bar, Row, RowAction } from '../gantt-diagram/project-gantt.component';
import {
    AddResourceDialogComponent,
    AddResourceDialogData,
    AddResourceDialogResult,
    ResourceRef,
} from '../gantt-diagram/resource-dialog/add-resource.dialog';
import { ListHeaderComponent } from '../item-list/list-header';
import { ItemComponent } from '../item/item.component';
import {
    CellMark,
    ReusableTimelineComponent,
    SpecialRowRenderer,
} from '../timeline-component/timeline.component';

type ViewMode = 'EMPLOYEE' | 'PROJECT';

type MixedRawBar = {
    resource_type: 'USER' | 'EXT_CONTRACTOR';
    resource_id: number;

    row_kind?: 'RESOURCE' | 'GLOBAL_ASSIGNMENT' | 'PROJECT_DAY';

    // Project identification for grouping children
    project_id: number;
    project_number?: string | null;
    project_name?: string | null;

    work_item_id?: number | null;
    work_item_name?: string | null;
    work_type_id?: number | null;
    work_type_name?: string | null;

    // Bar shape (end is exclusive)
    start_date: string;
    end_date: string;

    // Optional metadata used for coloring/flags
    total_hours?: number | null;
    allocation_percent?: number | null;
    resource_name?: string | null;
    square_meters?: number | null;
    project_day_square_meters?: number | null;
    project_day_notes?: string | null;

    // Some backends return 0/1; we treat it as boolean when mapping to Bars.
    is_manual?: boolean | number;
    is_locked?: boolean | number;
};

type UnassignedEmployeeChip = { userId: number; name: string };

type VehicleChip = {
    id: number;
    name: string;
    code?: string;
    plate?: string;
    is_locked?: boolean;
    notes?: string;
};

type TeamUnit = {
    teamId: number | string;
    kind: 'PROJECT_DAY' | 'REAL' | 'SINGLETON';
    teamName: string;
    dirty?: boolean;
    vehicles: VehicleChip[];
    totalHours?: number;
    locked?: boolean;
    members?: Array<{
        resourceKey: string;
        resourceName: string;
        hours: number;
    }>;
};

type ProjectGroupVehicleDTO = {
    vehicle_id: number;
    is_locked?: boolean;
    notes?: string;
};

type ProjectGroupDayDTO = {
    date: string;
    team_id: number | string;
    team_kind: 'PROJECT_DAY' | 'REAL' | 'SINGLETON';
    team_name: string;
    locked?: boolean;
    total_hours?: number;
    vehicles: ProjectGroupVehicleDTO[];
};

// Represents a single row in the timeline (employee or project)

@Component({
    selector: 'app-utilization-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoPipe,
        ReusableTimelineComponent,
        MatSidenavContent,
        FilterComponent,
        MatSidenav,
        MatSidenavContainer,
        ListHeaderComponent,
        MatButtonToggleGroup,
        MatButtonToggle,
        MatIcon,
    ],
    template: `
        <div
            class="flex min-w-0 flex-auto flex-col rounded-2xl bg-white shadow dark:border dark:bg-opacity-5"
        >
            <!-- Header -->
            @if (showListHeader) {
                <app-list-header
                    [isLoading]="isLoading || silentProgress"
                    [entityTitle]="'NAVIGATION.UTILIZATION_OVERVIEW'"
                    [searchPlaceholder]="'SEARCH'"
                    [filterToggle]="true"
                    (filterToggleClick)="onFilterToggle()"
                    [filterActive]="
                        filterObject && Object.keys(filterObject).length > 0
                    "
                >
                </app-list-header>
            }

            <!-- Main -->
            <div class="flex flex-auto overflow-hidden">
                <!-- Side Drawer for Filters -->
                <mat-sidenav-container
                    class="bg-card h-full w-full dark:bg-transparent"
                >
                    <mat-sidenav
                        mode="side"
                        [opened]="isSidenavOpened"
                        class="w-full sm:w-72"
                    >
                        <app-filter
                            [fields]="filterFields"
                            [initialFilters]="filterObject"
                            (filterSubmit)="onFilterSubmit($event)"
                        ></app-filter>
                    </mat-sidenav>

                    <mat-sidenav-content style="min-height: 70vh">
                        @if (!isLoading) {
                            <!-- View switcher (Angular Material) -->
                            <div
                                class="flex items-center justify-between px-4 py-4"
                            >
                                <mat-button-toggle-group
                                    class="!rounded-lg !border !border-gray-200"
                                    [value]="viewMode()"
                                    (change)="setViewMode($event.value)"
                                    aria-label="Select utilization view"
                                >
                                    <mat-button-toggle value="EMPLOYEE">
                                        {{
                                            'UTILIZATION_OVERVIEW.EMPLOYEE_PROJECT'
                                                | transloco
                                        }}
                                    </mat-button-toggle>

                                    <mat-button-toggle value="PROJECT">
                                        {{
                                            'UTILIZATION_OVERVIEW.PROJECT_EMPLOYEE'
                                                | transloco
                                        }}
                                    </mat-button-toggle>
                                </mat-button-toggle-group>

                                <!-- Optional helper label to make the current grouping obvious -->
                                <!--                                <span class="text-xs text-gray-500">-->
                                <!--                                    Grouping:-->
                                <!--                                    {{-->
                                <!--                                        viewMode() === 'EMPLOYEE'-->
                                <!--                                            ? 'by employee'-->
                                <!--                                            : 'by project'-->
                                <!--                                    }}-->
                                <!--                                </span>-->
                            </div>

                            <!-- TIMELINE COMPONENT -->
                            <div class="min-h-0 flex-1">
                                <app-reusable-timeline
                                    [titleText]="
                                        viewMode() === 'EMPLOYEE'
                                            ? transloco.translate('EMPLOYEES')
                                            : transloco.translate(
                                                  'PROJECTS_LABEL'
                                              )
                                    "
                                    [showAddButton]="false"
                                    [enableCellClick]="viewMode() === 'PROJECT'"
                                    [enableBarClick]="viewMode() === 'PROJECT'"
                                    [enableGhostClick]="false"
                                    [colW]="colW"
                                    [dates]="dates()"
                                    [rows]="rows()"
                                    [planningPermissions]="planningPermissions"
                                    [visibleBars]="visibleBars"
                                    [barColor]="barColor"
                                    [rowBarClass]="rowBarClass"
                                    [perBarClass]="perBarClass"
                                    [colIndex]="colIndex"
                                    [spanCols]="spanCols"
                                    [specialRowRenderers]="specialRowRenderers"
                                    [loading]="isLoading || silentProgress"
                                    [holidayByDate]="holidayDict"
                                    [absencesForRowDate]="absencesForRowDate"
                                    [isPast]="isPast"
                                    [isWeekend]="isWeekendDay"
                                    [isDividerRow]="isDividerRow"
                                    [isTeamSummaryRow]="isTeamSummaryRow"
                                    [teamsForDate]="teamsForDateResolver"
                                    [dirty]="
                                        viewMode() === 'PROJECT' && teamsDirty
                                    "
                                    (editBar)="
                                        editBarAtPointer(
                                            $event.$event,
                                            $event.bar,
                                            $event.row
                                        )
                                    "
                                    (addResourceDay)="
                                        openAddResourceDialog(
                                            'day',
                                            $event.date,
                                            $event.row,
                                            $event.hours,
                                            $event.squares
                                        )
                                    "
                                    (actionEmit)="handleAction($event)"
                                    (teamAction)="onTeamAction($event)"
                                    (apply)="applyTeamsChanges()"
                                />
                            </div>
                        }

                        <!-- UNASSIGNED EMPLOYEES TEMPLATE -->
                        <ng-template
                            #unassignedEmployeeTpl
                            let-e
                            let-date="date"
                            let-row="row"
                        >
                            <button
                                #btn
                                type="button"
                                [disabled]="true"
                                class="flex h-8 w-full items-center border border-amber-300 bg-amber-50 px-2 text-[11px] font-semibold text-amber-800 shadow-sm transition"
                                [ngClass]="{
                                    'hover:bg-amber-100 hover:ring-2 hover:ring-amber-300':
                                        !btn.disabled,
                                }"
                                [title]="e.name + ' • Unassigned'"
                            >
                                <!-- name -->
                                <span class="flex-1 truncate text-left">
                                    {{ e.name }}
                                </span>

                                <!-- optional badge -->
                                <!--                                <span-->
                                <!--                                    class="rounded bg-amber-200 px-1 text-[9px] font-bold text-amber-900"-->
                                <!--                                >-->
                                <!--                                    FREE-->
                                <!--                                </span>-->
                            </button>
                        </ng-template>

                        <!-- PROJECT DAY NOTES TEMPLATE -->
                        <ng-template
                            #projectDayNoteTpl
                            let-it
                            let-date="date"
                            let-row="row"
                        >
                            <!-- Stretch button to occupy the whole day cell for consistent row height -->
                            <button
                                type="button"
                                [disabled]="!planningPermissions.canAddNotes"
                                class="flex h-full min-h-8 w-full items-center gap-1 rounded-none border px-0 px-2 text-[11px] font-semibold shadow-sm hover:ring-2"
                                [ngClass]="
                                    it.value
                                        ? 'border-sky-300 bg-sky-50 text-sky-900 hover:ring-sky-200'
                                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:ring-slate-200'
                                "
                                (click)="
                                    planningPermissions.canAddNotes &&
                                        openAddResourceDialog('day', date, row)
                                "
                                [title]="
                                    it.value
                                        ? (it.value | slice: 0 : 120)
                                        : transloco.translate('ADD_NOTE')
                                "
                            >
                                <mat-icon
                                    class="icon-size-4"
                                    svgIcon="heroicons_outline:pencil-square"
                                    [ngClass]="
                                        it.value
                                            ? 'text-sky-500'
                                            : 'text-slate-400'
                                    "
                                ></mat-icon>
                                <span class="flex-1 truncate text-left">
                                    {{
                                        it.value
                                            ? it.value
                                            : transloco.translate('ADD_NOTE')
                                    }}
                                </span>
                            </button>
                        </ng-template>

                        <!-- PROJECT DAY M2 TEMPLATE -->
                        <ng-template
                            #projectDayM2Tpl
                            let-it
                            let-date="date"
                            let-row="row"
                        >
                            <!-- Button spans the whole cell so clicking anywhere opens the edit dialog -->
                            <button
                                type="button"
                                class="flex h-full min-h-8 w-full items-center gap-1 rounded-none border px-0 px-2 text-[11px] font-semibold shadow-sm hover:ring-2"
                                [ngClass]="
                                    it.value != null
                                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:ring-emerald-200'
                                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:ring-slate-200'
                                "
                                (click)="
                                    openAddResourceDialog('day', date, row)
                                "
                                [title]="
                                    it.value != null
                                        ? it.value + ' m²'
                                        : this.transloco.translate('ADD_M2')
                                "
                            >
                                <mat-icon
                                    class="icon-size-4"
                                    svgIcon="heroicons_outline:pencil-square"
                                    [ngClass]="
                                        it.value != null
                                            ? 'text-emerald-500'
                                            : 'text-slate-400'
                                    "
                                ></mat-icon>
                                <span class="flex-1 truncate text-left">
                                    {{
                                        it.value != null
                                            ? it.value + ' m²'
                                            : this.transloco.translate('ADD_M2')
                                    }}
                                </span>
                            </button>
                        </ng-template>
                    </mat-sidenav-content>
                </mat-sidenav-container>
            </div>
        </div>
    `,
})
export class UtilizationDashboardComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private dialog = inject(MatDialog);
    private utils = inject(Utils);
    public transloco = inject(TranslocoService);
    public toastr = inject(ToastrService);
    private cap = inject(CapacityService);
    private projectsService = inject(ProjectsService);
    private capacityService = inject(CapacityService);
    private usersService = inject(UsersService);
    private externalContractorsService = inject(ExternalContractorsService);
    private _filterPrefs = inject(FilterPrefsService);
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _holidaysService = inject(HolidaysService);
    private _absencesLeaveService = inject(AbsencesLeaveService);
    private planningPermissionsService = inject(PlanningPermissionsService);

    get planningPermissions(): PlanningPermissions {
        return this.planningPermissionsService.getPermissions();
    }

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /** Debounce stream for persisting the full filter-set */
    private readonly _filterSave$ = new Subject<Record<string, any>>();

    readonly listId = 'utilization-dashboard';
    isSidenavOpened: boolean = false;
    filterObject: any = {};
    filterFields: FilterField[] = [
        {
            name: 'employee_ids',
            type: 'multi-select',
            label: 'FILTER.UTILIZATION_DASHBOARD.SELECT_EMPLOYEES',
            placeholder: 'FILTER.UTILIZATION_DASHBOARD.SELECT_EMPLOYEES',
            options: [],
            dynamicOptions: true,
            optionsProperty: 'employees',
        },
        {
            name: 'project_ids',
            type: 'multi-select',
            label: 'FILTER.UTILIZATION_DASHBOARD.SELECT_PROJECTS',
            placeholder: 'FILTER.UTILIZATION_DASHBOARD.SELECT_PROJECTS',
            options: [],
            dynamicOptions: true,
            optionsProperty: 'projects',
        },
        // {
        //     name: 'contractor_ids',
        //     type: 'multi-select',
        //     label: 'External Contractors',
        //     placeholder: 'Select contractors',
        //     options: [],
        //     dynamicOptions: true,
        //     optionsProperty: 'externalContractors',
        // },
        {
            name: 'from_to',
            type: 'date-range',
            label: 'FILTER.UTILIZATION_DASHBOARD.DATE_RANGE',
            // placeholders for the two inputs
            placeholderFrom: 'FROM',
            placeholderTo: 'TO',
            // require both ends of the range
            validatorsFrom: [Validators.required],
            validatorsTo: [],
        },
    ];

    isLoading = false;
    silentProgress = false;

    // Flag to control if past days are editable or not
    disablePastEditing = true;

    specialRowRenderers: SpecialRowRenderer[] = [];
    private readonly UNASSIGNED_KEY = 'SPECIAL:UNASSIGNED_EMPLOYEES';

    @ViewChild('unassignedEmployeeTpl', { static: true })
    unassignedEmployeeTpl!: TemplateRef<any>;
    @ViewChild('projectDayNoteTpl', { static: true })
    projectDayNoteTpl!: TemplateRef<any>;
    @ViewChild('projectDayM2Tpl', { static: true })
    projectDayM2Tpl!: TemplateRef<any>;
    unassignedEmployeesRenderer: SpecialRowRenderer = {
        key: this.UNASSIGNED_KEY,

        itemsForDate: (date: string) => {
            return this.unassignedEmployeesByDate[date] ?? [];
        },

        trackBy: (_: number, u) => u.userId,

        labelField: 'name',

        template: undefined,
    };

    projectDaySquareMetersRenderer: SpecialRowRenderer = {
        key: 'PROJECT_DAY_M2',

        itemsForDate: (date: string, row?: Row) => {
            // Skip until template + row meta are available to avoid fallback rows.
            if (!this.projectDayM2Tpl || !row?.meta) return [];

            const { projectId, workItemId } = row.meta;
            const key = `${projectId}|${workItemId ?? 0}|${date}`;
            const value = this.indexProjectDayM2.get(key);

            // Always return a single item so the template renders a clickable cell even when empty.
            return [{ value: value ?? null }];
        },

        trackBy: (_: number, it: any) => it.value,

        // Template-driven renderer; leave labelField undefined to avoid fallback.
        labelField: undefined,

        template: undefined,
    };

    projectDayNotesRenderer: SpecialRowRenderer = {
        key: 'PROJECT_DAY_NOTES',

        itemsForDate: (date: string, row?: Row) => {
            // Avoid rendering until template is available to prevent fallback label row.
            if (!this.projectDayNoteTpl) return [];
            if (!row?.meta) return [];
            const { projectId, workItemId } = row.meta;
            const key = `${projectId}|${workItemId ?? 0}|${date}`;
            const value = this.indexProjectDayNotes.get(key);
            // Always return an item so template renders a clickable cell even when empty.
            return [{ value: value ?? '' }];
        },

        trackBy: (_: number, it: any) => it.value,

        // Template-driven renderer; leave labelField undefined to avoid fallback.
        labelField: undefined,
    };

    // date(YYYY-MM-DD) -> list of employees unassigned that day
    private unassignedEmployeesByDate: Record<
        string,
        UnassignedEmployeeChip[]
    > = {};

    @Input() users!: User[];
    @Input() projects!: Project[];
    @Input() from?: string; // optional range start (YYYY-MM-DD)
    @Input() to?: string; // optional range end (YYYY-MM-DD)
    @Input() showListHeader: boolean = true;
    @Input() showHolidays = signal<boolean>(false);
    @Input() showAbsences = signal<boolean>(true);
    absencesByUser = new Map<number, Absence[]>();
    // user_id -> (YYYY-MM-DD -> CellMark[])
    absenceDayIndex = new Map<number, Record<string, CellMark[]>>();

    holidayDict: Record<string, Holiday[]> = {};

    colW = 100; // column width in pixels

    // Current grouping mode for the dashboard.
    // EMPLOYEE: Users as parents, projects as children (current behavior)
    // PROJECT:  Projects as parents, users as children (new behavior)
    viewMode = signal<ViewMode>('EMPLOYEE');

    // Cache last backend response so switching views does not refetch.
    private _lastRaw: MixedRawBar[] = [];
    private _lastFrom: string | null = null;
    private _lastTo: string | null = null;

    // ---------------------------------------------------------------------
    // RAW INDEXES (built once per load)
    // ---------------------------------------------------------------------

    private indexByProject = new Map<number, MixedRawBar[]>();
    private indexByProjectWorkItem = new Map<string, MixedRawBar[]>();
    private indexProjectDayM2 = new Map<string, number>();
    private indexProjectDayNotes = new Map<string, string>();

    // Signals to hold date columns and row data
    dates = signal<string[]>([]);
    rows = signal<Row[]>([]);

    pendingChanges = signal<ApplyItem[]>([]);

    teamsDirty = false;
    private dirtyTeamProjects = new Set<number>();
    private projectTeamsByDate = new Map<number, Record<string, TeamUnit[]>>();

    ngOnInit() {
        this.load();
    }

    ngAfterViewInit(): void {
        this.unassignedEmployeesRenderer.template = this.unassignedEmployeeTpl;
        this.projectDayNotesRenderer.template = this.projectDayNoteTpl;
        this.projectDaySquareMetersRenderer.template = this.projectDayM2Tpl;

        this.specialRowRenderers = [
            this.unassignedEmployeesRenderer,
            this.projectDaySquareMetersRenderer,
            this.projectDayNotesRenderer,
        ];

        console.log('Special row renderers:', this.specialRowRenderers);
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(1);
        this._unsubscribeAll.complete();
    }

    // === Utility date helpers ===

    private todayISO(): string {
        const d = new Date();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    }

    private addDays(iso: string, delta: number): string {
        const d = new Date(iso);
        d.setDate(d.getDate() + delta);
        return d.toISOString().slice(0, 10);
    }

    private addMonths(iso: string, months: number): string {
        const d = new Date(iso);
        const day = d.getDate();
        d.setMonth(d.getMonth() + months);
        // Adjust for shorter months (e.g., from Jan 31 → Feb 28)
        if (d.getDate() < day) d.setDate(0);
        return d.toISOString().slice(0, 10);
    }

    /**
     * Build fast lookup indexes from raw backend data.
     * This MUST be called once inside load().
     */
    private buildRawIndexes(raw: MixedRawBar[]): void {
        this.indexByProject.clear();
        this.indexByProjectWorkItem.clear();
        this.indexProjectDayM2.clear();
        this.indexProjectDayNotes.clear();

        // Filter out rows to not include GLOBAL_ASSIGNMENT row_kind of row
        raw = raw.filter((r) => r.row_kind !== 'GLOBAL_ASSIGNMENT');

        for (const r of raw) {
            // -------------------------------
            // Project index
            // -------------------------------
            if (!this.indexByProject.has(r.project_id)) {
                this.indexByProject.set(r.project_id, []);
            }
            this.indexByProject.get(r.project_id)!.push(r);

            // -------------------------------
            // Project + Work Item index
            // -------------------------------
            const wid = r.work_item_id ?? 0;
            const pwKey = `${r.project_id}|${wid}`;

            if (!this.indexByProjectWorkItem.has(pwKey)) {
                this.indexByProjectWorkItem.set(pwKey, []);
            }
            this.indexByProjectWorkItem.get(pwKey)!.push(r);

            // -------------------------------
            // Project-day square meters index
            // -------------------------------
            if (r.project_day_square_meters != null) {
                // Expand [start_date, end_date) into actual days
                for (const day of this.eachDayExclusive(
                    r.start_date,
                    r.end_date
                )) {
                    const m2Key = `${r.project_id}|${wid}|${day}`;
                    this.indexProjectDayM2.set(
                        m2Key,
                        r.project_day_square_meters
                    );
                }
            }

            // -------------------------------
            // Project-day notes index
            // -------------------------------
            if (r.project_day_notes != null) {
                for (const day of this.eachDayExclusive(
                    r.start_date,
                    r.end_date
                )) {
                    const noteKey = `${r.project_id}|${wid}|${day}`;
                    this.indexProjectDayNotes.set(
                        noteKey,
                        String(r.project_day_notes ?? '')
                    );
                }
            }
        }

        console.log('Project-day square meters index:', this.indexProjectDayM2);
        console.log('Project-day notes index:', this.indexProjectDayNotes);
    }

    /**
     * Returns stored square meters for given project / work item / day.
     * If nothing exists yet, returns null.
     */
    private getProjectDaySquareMeters(
        projectId: number,
        workItemId: number,
        date: string
    ): number | null {
        const key = `${projectId}|${workItemId ?? 0}|${date}`;
        return this.indexProjectDayM2.get(key) ?? null;
    }

    /** Returns stored note for given project / work item / day. */
    private getProjectDayNote(
        projectId: number,
        workItemId: number,
        date: string
    ): string | null {
        const key = `${projectId}|${workItemId ?? 0}|${date}`;
        return this.indexProjectDayNotes.get(key) ?? null;
    }

    // Builds an inclusive list of dates from "from" to "to"
    private buildDateTicks(from: string, to: string): string[] {
        const parse = (s: string) => {
            const [y, m, d] = s.split('-').map(Number);
            return new Date(Date.UTC(y, m - 1, d));
        };
        const s = parse(from),
            e = parse(to);
        const out: string[] = [];
        for (
            let d = new Date(s);
            d.getTime() <= e.getTime();
            d.setUTCDate(d.getUTCDate() + 1)
        ) {
            out.push(d.toISOString().slice(0, 10));
        }
        return out;
    }

    /**
     * Builds an array of ISO dates between start (inclusive) and endExclusive (exclusive),
     * using UTC-based day increments to avoid timezone drift.
     */
    private eachDayExclusive(start: string, endExclusive: string): string[] {
        const [ys, ms, ds] = start.split('-').map(Number);
        const [ye, me, de] = endExclusive.split('-').map(Number);
        const out: string[] = [];
        let cur = new Date(Date.UTC(ys, ms - 1, ds));
        const end = new Date(Date.UTC(ye, me - 1, de));

        // Increment by 1 day in UTC until reaching endExclusive
        for (; cur < end; cur.setUTCDate(cur.getUTCDate() + 1)) {
            out.push(cur.toISOString().slice(0, 10));
        }

        return out;
    }

    // Determines which bars should be visible (e.g., only those with >0 hours)
    visibleBars = (r: Row) => {
        // Skip overlay bars for special per-day rows; dedicated templates render their content.
        if (
            r.meta?.type === 'PROJECT_DAY_M2' ||
            r.meta?.type === 'PROJECT_DAY_NOTES'
        ) {
            return [];
        }

        // Default behavior for normal rows
        return r.bars?.filter(
            (b) => (b.total_hours ?? 0) > 0 || (b.square_meters ?? 0) > 0
        );
    };

    // Assigns color to bars based on hours or manual flag
    barColor = (hours?: number | null, isManual?: any) => {
        // if (isManual) return 'bg-emerald-200 border-emerald-500';
        // if (!hours) return 'bg-indigo-200 border-indigo-400';
        // if (hours >= 8) return 'bg-amber-200 border-amber-500';
        // if (hours >= 4) return 'bg-indigo-200 border-indigo-500';
        return 'bg-amber-200 border-amber-500'; // 'bg-sky-200 border-sky-500';
    };

    // Finds the column index of a given date
    colIndex = (d: string | Date): number => {
        const key =
            typeof d === 'string' ? d : new Date(d).toISOString().slice(0, 10);
        return this.dates().indexOf(key);
    };

    /**
     * Calculates how many date columns a bar should span on the timeline.
     * Uses UTC-safe day math to align perfectly with the generated date grid.
     */
    spanCols = (start: string | Date, endExclusive: string | Date): number => {
        const sKey =
            typeof start === 'string'
                ? start
                : new Date(start).toISOString().slice(0, 10);
        const eKey =
            typeof endExclusive === 'string'
                ? endExclusive
                : new Date(endExclusive).toISOString().slice(0, 10);

        const s = this.colIndex(sKey);
        const e = this.colIndex(this.prevDayUTC(eKey)); // `endExclusive` means end - 1 day

        if (s < 0 || e < 0) return 1;
        return Math.max(1, e - s + 1);
    };

    retrieveDynamicOptions(): void {
        // Retrieve the dynamic options
        this.capacityService
            .getUtilizationFilterOptions()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                if (response.status) {
                    console.log(response);
                    // Update filterFields with options
                    this.filterFields.forEach((field) => {
                        if (field.name === 'employee_ids') {
                            // We need to pass options from users array retrieved
                            field.options = this.users?.map((user) => ({
                                key: user.id.toString(),
                                value: `${user.first_name} ${user.last_name}`,
                            }));
                        } else if (field.name === 'contractor_ids') {
                            const contractors: ExternalContractor[] =
                                response.options['externalContractors'] || [];
                            field.options = contractors.map((contractor) => ({
                                key: contractor.id.toString(),
                                value: contractor.name,
                            }));
                        } else if (field.name === 'project_ids') {
                            // We need to pass options from projects array retrieved
                            field.options = this.projects?.map((project) => ({
                                key: project.id.toString(),
                                value:
                                    project.project_number ||
                                    `${project.project_number}`,
                            }));
                        } else {
                            if (field.dynamicOptions && field.optionsProperty) {
                                const options =
                                    response.options[field.optionsProperty] ||
                                    [];
                                field.options = options.map((value) => ({
                                    key: value,
                                    value: value,
                                }));
                            }
                        }
                    });

                    // Mark for check
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    async load() {
        // Retrieve users and contractors
        this.silentProgress = true;
        this.isLoading = true;
        this.dirtyTeamProjects.clear();
        this.teamsDirty = false;
        this.projectTeamsByDate.clear();

        try {
            let data = await Promise.all([
                firstValueFrom(this.usersService.getAllUsers()),
                firstValueFrom(
                    this.projectsService.getProjects(0, 0, 'id', 'asc', '', {
                        use_pagination: '0',
                    })
                ),
                // firstValueFrom(this.externalContractorsService.getAllExternalContractors())
            ]);

            this.users = data[0]?.users ?? [];
            this.projects = data[1]?.data ?? [];
            // this.contractors.set(data[1]?.data ?? []);

            // Retrieve dynamic options for filters
            this.retrieveDynamicOptions();
        } catch (e) {
            console.error('Error loading users or contractors', e);
            this.users = [];
            // this.contractors.set([]);
            this.silentProgress = false;
        }

        // If no users provided, there is nothing to fetch
        if (!Array.isArray(this.users) || this.users.length === 0) {
            this.rows.set([]);
            this.silentProgress = false;
            return;
        }

        console.log('filterObject:', this.filterObject);

        // Based on the filter object, we should set the filter parameters
        let { from_to_start, from_to_end, employee_ids, project_ids } =
            this.filterObject;

        if (from_to_start) {
            // we need to get only the date part in YYYY-MM-DD
            from_to_start = this.utils.formatDateForBackEnd(from_to_start);
            // splice it to only date part
            from_to_start = from_to_start.slice(0, 10);
        }
        if (from_to_end) {
            from_to_end = this.utils.formatDateForBackEnd(from_to_end);
            from_to_end = from_to_end.slice(0, 10);
        }

        // Resolve date range (defaults relative to today)
        const today = this.todayISO();
        const defaultFrom = this.addDays(today, -2);
        const defaultTo = this.addMonths(today, 4);
        let from = this.from ?? from_to_start ?? defaultFrom;
        let to = this.to ?? from_to_end ?? defaultTo;

        // Build date columns first so span/index helpers work
        this.dates.set(this.buildDateTicks(from, to));

        const itemsPayload = [
            {
                type: 'USER',
                ids: employee_ids ? employee_ids : this.users.map((u) => u.id),
            },
        ];

        // Fire one POST call to fetch all raw bars
        const resp = await firstValueFrom(
            this.cap.getResourcesGanttRawMixed(
                itemsPayload,
                project_ids,
                from,
                to
            )
        );

        // lets retrieve holidays and potential user absences here if needed
        if (this.showHolidays()) {
            await this.getHolidays(from, to);
        }
        if (this.showAbsences()) {
            await this.getUserAbsences(from, to);
        }

        // Defensive checks
        const raw: MixedRawBar[] = Array.isArray(resp?.data)
            ? (resp.data as MixedRawBar[])
            : [];

        // Cache the response so we can rebuild rows when the view changes (no new API call)
        this._lastRaw = raw;
        this._lastFrom = from;
        this._lastTo = to;

        // Build indexes ONCE
        this.buildRawIndexes(raw);

        // Build rows
        this.rows.set(this.buildRowsForView(raw, from, to));
        console.log('[this.rows] out:', this.rows());

        // Build unassigned employees
        this.buildUnassignedEmployeesIndex(raw, from, to);

        if (this.viewMode() === 'PROJECT') {
            // Vehicle teams are rendered only in Project view.
            await this.loadTeamsForProjects(from, to);
        }

        this.silentProgress = false;
        this.isLoading = false;
    }

    private ensureUnassignedEmployeesRow(rows: Row[]): Row[] {
        if (!this.planningPermissions.canSeeUnassignedResources) {
            return rows;
        }

        if (rows.some((r) => r.resourceKey === this.UNASSIGNED_KEY)) {
            return rows;
        }

        return [
            ...rows,
            {
                resourceKey: this.UNASSIGNED_KEY,
                resourceName: this.transloco.translate('UNASSIGNED_EMPLOYEES'),
                class: 'bg-slate-50 font-semibold',
                bars: [],
            },
        ];
    }

    /**
     * Builds date -> employees with NO allocation on that date.
     * "Unassigned" means: the user has no raw record covering that date.
     * Works with raw=[] (then all users are unassigned every day).
     */
    private buildUnassignedEmployeesIndex(
        raw: MixedRawBar[],
        from: string,
        to: string
    ): void {
        if (!this.planningPermissions.canSeeUnassignedResources) {
            this.unassignedEmployeesByDate = {};
            return;
        }

        const visibleDates: string[] = this.dates(); // already built with buildDateTicks(from,to)
        const APPROVED = this.utils.ABSENCE_STATUS_APPROVED;
        const REJECTED = this.utils.ABSENCE_STATUS_REJECTED;

        // Scope users: honor the selected employee filter; otherwise evaluate
        // against the full loaded user list so unassigned users do not disappear
        // once the backend returns only scheduled resources in `raw`.
        const scopedUserIds = new Set<number>();
        if (this.filterObject.employee_ids?.length) {
            this.filterObject.employee_ids.forEach((id: any) =>
                scopedUserIds.add(Number(id))
            );
        }
        if (!scopedUserIds.size) {
            (this.users ?? []).forEach((u) => scopedUserIds.add(Number(u.id)));
        }

        // date -> set(userId) that IS assigned to at least one project on that date
        const assignedByDate = new Map<string, Set<number>>();

        for (const r of raw ?? []) {
            // Only consider user allocations
            if (r.resource_type !== 'USER') continue;

            // Expand [start_date, end_date) into individual days (end is exclusive)
            for (const day of this.eachDayExclusive(r.start_date, r.end_date)) {
                // Only keep dates that are visible in the timeline (avoids growing map)
                // If you want speed, turn visibleDates into a Set first.
                if (day < from || day > to) continue;

                if (!assignedByDate.has(day))
                    assignedByDate.set(day, new Set<number>());
                assignedByDate.get(day)!.add(Number(r.resource_id));
            }
        }

        const out: Record<string, UnassignedEmployeeChip[]> = {};

        for (const d of visibleDates) {
            const assignedSet = assignedByDate.get(d) ?? new Set<number>();
            const absentSet = this.absentUsersForDate(d, APPROVED, REJECTED);

            // Build the list of chips for that date
            out[d] = (this.users ?? [])
                .filter((u) => scopedUserIds.has(Number(u.id)))
                .filter((u) => !assignedSet.has(Number(u.id)))
                .filter((u) => !absentSet.has(Number(u.id)))
                .map((u) => ({
                    userId: Number(u.id),
                    name: `${u.first_name} ${u.last_name}`,
                }));
        }

        this.unassignedEmployeesByDate = out;

        console.log(
            'unassignedEmployeesByDate:',
            this.unassignedEmployeesByDate
        );
    }

    /**
     * Timeline hook: returns unassigned employees for a specific date.
     */
    unassignedEmployeesForDate = (
        date: string | Date
    ): UnassignedEmployeeChip[] => {
        const d =
            typeof date === 'string' ? date : date.toISOString().slice(0, 10);
        return this.unassignedEmployeesByDate[d] ?? [];
    };

    /** Build absent-set for a given date using absenceDayIndex */
    private absentUsersForDate(
        d: string,
        APPROVED: number,
        REJECTED: number
    ): Set<number> {
        const out = new Set<number>();
        for (const [uid, perDay] of this.absenceDayIndex.entries()) {
            const marks = perDay?.[d] ?? [];
            const hasApproved = marks.some(
                (m) =>
                    m.status === APPROVED ||
                    (m.status !== REJECTED && m.status !== undefined) ||
                    m.status === undefined
            );
            if (hasApproved) out.add(uid);
        }
        return out;
    }

    isUnassignedEmployeesRow = (r: Row) =>
        r.kind === 'UNASSIGNED' && r.resourceKey === 'UNASSIGNED_EMPLOYEES';

    // Groups raw capacity data by project and sorts by start date
    private groupByProject(
        raw: any[]
    ): Array<{ project_id: number; project_number: string; bars: Bar[] }> {
        const map = new Map<
            number,
            { project_id: number; project_number: string; bars: Bar[] }
        >();
        for (const b of raw) {
            const pid = Number(b.project_id);
            const pname = b.project_number ?? `Project #${pid}`;
            if (!map.has(pid))
                map.set(pid, {
                    project_id: pid,
                    project_number: pname,
                    bars: [],
                });
            map.get(pid)!.bars.push({
                start_date: b.start_date,
                end_date: b.end_date,
                total_hours: b.total_hours ?? null,
                square_meters: b.square_meters ?? null,
                allocation_percent: b.allocation_percent ?? null,
                is_manual: b.is_manual ?? false,
                is_locked: b.is_locked ?? false,
                resource_name: b.resource_name,
            });
        }
        for (const v of map.values()) {
            v.bars.sort((a, b) => a.start_date.localeCompare(b.start_date));
        }
        return Array.from(map.values()).sort((a, b) =>
            a.project_number.localeCompare(b.project_number)
        );
    }

    private addDaysUTC(iso: string, n: number): string {
        // Parse ISO date (YYYY-MM-DD) and shift by n days in UTC
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() + n);
        return dt.toISOString().slice(0, 10);
    }

    /**
     * Aggregates project bars per employee into daily totals (UTC-based).
     *
     * - Distributes multi-day bars evenly across days
     * - Sums both HOURS and SQUARE METERS per calendar day
     * - Produces 1-day bars (end date is exclusive)
     */
    private aggregateEmployeeBars(raw: any[], from: string, to: string): Bar[] {
        const dates = this.dates();

        // Map<YYYY-MM-DD, { hours, square_meters }>
        // Used to accumulate daily totals across all project bars
        const byDay = new Map<
            string,
            { hours: number; square_meters: number }
        >();

        // ------------------------------------------------------------------
        // STEP 1: Aggregate raw records into per-day values
        // ------------------------------------------------------------------
        for (const rec of raw) {
            // Generate list of days covered by this bar
            // NOTE: start is inclusive, end is exclusive
            const days = this.eachDayExclusive(rec.start_date, rec.end_date);

            // Skip invalid or empty ranges
            if (!days.length) continue;

            // Distribute total hours evenly across days
            const perDayHours =
                typeof rec.total_hours === 'number'
                    ? rec.total_hours / days.length
                    : 0;

            // Distribute total square meters evenly across days
            // square_meters is resource-level, not project-level
            const perDaySquareMeters =
                typeof rec.square_meters === 'number'
                    ? rec.square_meters / days.length
                    : 0;

            // Accumulate values per day
            for (const d of days) {
                // Skip days outside the visible range
                if (d < from || d > to) continue;

                // Get existing accumulator or initialize a new one
                const acc = byDay.get(d) ?? {
                    hours: 0,
                    square_meters: 0,
                };

                // Add distributed values
                acc.hours += perDayHours;
                acc.square_meters += perDaySquareMeters;

                // Store back into the map
                byDay.set(d, acc);
            }
        }

        // ------------------------------------------------------------------
        // STEP 2: Convert aggregated values into 1-day UI bars
        // ------------------------------------------------------------------
        const bars: Bar[] = [];

        for (const day of dates) {
            const agg = byDay.get(day);
            if (!agg) continue;

            // Round values to 2 decimals for UI consistency
            const totalHours = Math.round(agg.hours * 100) / 100;
            const totalSquareMeters = Math.round(agg.square_meters * 100) / 100;

            // Skip empty days (no hours and no square meters)
            if (totalHours <= 0 && totalSquareMeters <= 0) continue;

            // Create a single-day bar (end date is exclusive)
            bars.push({
                start_date: day,
                end_date: this.addDaysUTC(day, 1),
                total_hours: totalHours,
                square_meters: totalSquareMeters,
            });
        }

        return bars;
    }

    /**
     * Returns the previous calendar day (UTC-based).
     */
    private prevDayUTC(iso: string): string {
        return this.addDaysUTC(iso, -1);
    }

    // Computes average hours per day for a record
    private perDayHours(rec: any): number {
        if (typeof rec.total_hours === 'number') {
            const n = Math.max(
                1,
                this.eachDayExclusive(rec.start_date, rec.end_date).length
            );
            return Math.round((rec.total_hours / n) * 100) / 100;
        }
        // Alternative calculation: using allocation_percent * capacity (if available)
        // if (rec.allocation_percent && rec.capacity) return rec.capacity * (rec.allocation_percent / 100);
        return 0;
    }

    rowBarClass = (r: Row) => {
        if (r.kind === 'EMPLOYEE') {
            // Examples (izberi enega):
            // a) “transparent” slog:
            // return 'bg-transparent border-transparent text-slate-600 shadow-none';

            // b) nevtralno sivo:
            return 'bg-slate-200/70 border-slate-300 text-slate-700';
        }
        // default for project rows → let barColor decide
        return '';
    };

    perBarClass = (r: Row, b: Bar) => {
        if (r.kind === 'EMPLOYEE' && (b.total_hours ?? 0) <= 1) {
            return null; // 'bg-sky-200/70 border-sky-400 text-slate-700'; // primer: “light work” day
        }
        return null;
    };

    onFilterToggle(): void {
        this.isSidenavOpened = !this.isSidenavOpened;
    }

    /**
     * Normalizes a date-range field without discarding single-bound selections.
     * - Accepts ISO strings or Date objects.
     * - If only one bound is present, keeps it and leaves the other as null.
     * - If both are present but reversed, swaps them.
     * - Emits flat keys: <field>_start and <field>_end (YYYY-MM-DD or your backend format).
     */
    normalizeDateRange(data: Record<string, any>, field: 'from_to'): void {
        const range = data?.[field];

        // Nothing selected → clear
        if (!range || (range.start == null && range.end == null)) {
            data[field] = null;
            data[`${field}_start`] = null;
            data[`${field}_end`] = null;
            return;
        }

        // Robust parser: handles DateTime, Date, ISO string, empty string
        const parseDT = (v: any): DateTime | null => {
            if (v == null) return null;

            // If already a Luxon DateTime
            if (
                v &&
                typeof v === 'object' &&
                typeof v.isValid === 'boolean' &&
                typeof v.toISO === 'function'
            ) {
                return v.isValid ? v : null;
            }

            // If plain JS Date
            if (v instanceof Date) {
                const dt = DateTime.fromJSDate(v);
                return dt.isValid ? dt : null;
            }

            // If string (trim and handle empty)
            if (typeof v === 'string') {
                const s = v.trim();
                if (s === '') return null;

                // 1) try ISO directly
                let dt = DateTime.fromISO(s);
                if (dt.isValid) return dt;

                // 2) fallback: let JS parse weird formats
                const js = new Date(s);
                if (!isNaN(+js)) {
                    dt = DateTime.fromJSDate(js);
                    if (dt.isValid) return dt;
                }

                return null;
            }

            // Unknown shape → last resort: try constructing Date
            try {
                const js = new Date(v);
                if (!isNaN(+js)) {
                    const dt = DateTime.fromJSDate(js);
                    return dt.isValid ? dt : null;
                }
            } catch {
                /* ignore */
            }

            return null;
        };

        let startDT = parseDT(range.start);
        let endDT = parseDT(range.end);

        // If both exist but reversed, swap them
        if (startDT && endDT && startDT > endDT) {
            const tmp = startDT;
            startDT = endDT;
            endDT = tmp;
        }

        // Format to date-only (backend-friendly); change to toISO() if you need time
        const fmt = (dt: DateTime | null) =>
            dt ? dt.toFormat('yyyy-LL-dd') : null;

        const start = fmt(startDT);
        const end = fmt(endDT);

        // If at least one bound exists, keep nested; else clear
        data[field] = start === null && end === null ? null : { start, end };
        data[`${field}_start`] = start;
        data[`${field}_end`] = end;

        // Optional: debug the actual shapes you are receiving
        // console.debug('[normalizeDateRange]', {
        //   raw: range,
        //   types: { start: typeof range.start, end: typeof range.end },
        //   parsed: { startDT: startDT?.toISO(), endDT: endDT?.toISO() },
        //   out: { start, end }
        // });
    }

    /**
     * Handles filter form submission.
     * Closes the side panel, applies normalized filters, and persists them if valid.
     * @param values - The submitted filter values
     */
    onFilterSubmit(values: any): void {
        console.log('[onFilterSubmit] values:', values);
        this.isSidenavOpened = false;

        if (values) {
            this.normalizeDateRange(values, 'from_to');

            // Check if all fields are null or empty → if true, skip applying filter
            const allFieldsAreNull = Object.values(values).every(
                (val) =>
                    val === null ||
                    val === undefined ||
                    val === '' ||
                    (Array.isArray(val) && val.length === 0)
            );

            if (!allFieldsAreNull) {
                this.filterObject = { ...values };
                this.load();
                this._filterSave$.next(this.filterObject);
            }
        } else {
            // If the values are null, reset the filter
            // check if the filterObject is not empty
            if (Object.keys(this.filterObject).length === 0) {
                console.warn('[onFilterSubmit] No filters to reset.');
                return;
            }
            this.filterObject = {};
            this._filterPrefs.clear(this.listId);
            this.load();
        }

        this._changeDetectorRef.markForCheck();
    }

    protected readonly Object = Object;

    /** Key that marks the special Teams summary row at the bottom */
    private readonly TEAM_SUMMARY_KEY = 'TEAMS_SUMMARY';

    /** Storage: teams per date */
    private teamsByDate = new Map<
        string,
        Array<{
            teamId: number;
            teamName: string;
            totalHours: number;
            locked?: boolean;
            vehicles?: Array<{ code: string }>;
            members: Array<{
                resourceKey: string;
                resourceName: string;
                hours: number;
            }>;
        }>
    >();

    /** Hook for divider rows in the timeline */
    isDividerRow = (row: Row) => row.kind === 'DIVIDER';

    /** Hook for the timeline: identify the teams summary row */
    isTeamSummaryRow = (row: Row) => row.kind === 'TEAMS';

    /** Hook for the timeline: return teams for a given date */
    private teamsForDateInternal(date: string | Date, row?: Row) {
        const iso =
            typeof date === 'string'
                ? date.slice(0, 10)
                : new Date(date).toISOString().slice(0, 10);

        const projectId = row?.meta?.projectId;
        if (!projectId) {
            return [];
        }

        const byDate = this.projectTeamsByDate.get(projectId) ?? {};
        const teams = byDate[iso] ?? [];

        // Map internal team units to the shape expected by the timeline.
        return teams.map((t) => ({
            teamId: t.teamId,
            teamName: t.teamName,
            totalHours: t.totalHours ?? 0,
            locked: t.locked ?? false,
            vehicles: (t.vehicles ?? []).map((v) => ({
                id: v.id,
                name: v.name,
                plate: v.plate,
                is_locked: v.is_locked,
                notes: v.notes,
            })),
            members: t.members ?? [],
        }));
    }

    /** Exposed resolver passed to the timeline; re-assigned to trigger OnPush checks when data changes. */
    teamsForDateResolver = (date: string | Date, row?: Row) =>
        this.teamsForDateInternal(date, row);

    /** Recreate resolver reference to notify child OnPush components that teams data changed. */
    private bumpTeamsResolver() {
        this.teamsForDateResolver = (date: string | Date, row?: Row) =>
            this.teamsForDateInternal(date, row);
        this._changeDetectorRef.markForCheck();
    }

    /** Optional click handler from the timeline (open an edit dialog if you want) */
    onEditTeam(e: { date: string | Date; teamId: number; teamName: string }) {
        console.log('[Teams] Edit team:', e);
    }

    private projectTeamName(): string {
        return this.transloco.translate('VEHICLES_LABEL');
    }

    private markTeamsDirty(projectId: number) {
        // Track projects with unsaved vehicle changes so the Apply button can surface.
        this.dirtyTeamProjects.add(projectId);
        this.teamsDirty = this.dirtyTeamProjects.size > 0;
    }

    private clearProjectDirty(projectId: number) {
        this.dirtyTeamProjects.delete(projectId);
        this.teamsDirty = this.dirtyTeamProjects.size > 0;
    }

    private ensureProjectDayTeam(projectId: number, date: string): TeamUnit {
        const byDate = this.projectTeamsByDate.get(projectId) ?? {};
        const dayTeams = byDate[date] ?? [];
        let unit = dayTeams.find((t) => t.kind === 'PROJECT_DAY');
        if (!unit) {
            // Guarantee a default team so vehicles can be added even on empty days.
            unit = {
                teamId: 1,
                kind: 'PROJECT_DAY',
                teamName: this.projectTeamName(),
                vehicles: [],
            };
            const nextDayTeams = [...dayTeams, unit];
            this.projectTeamsByDate.set(projectId, {
                ...byDate,
                [date]: nextDayTeams,
            });
        }
        return unit;
    }

    private initProjectTeams(projectId: number, days: string[]) {
        const byDate: Record<string, TeamUnit[]> = {};
        const name = this.projectTeamName();

        for (const d of days) {
            byDate[d] = [
                {
                    teamId: 1,
                    kind: 'PROJECT_DAY',
                    teamName: name,
                    vehicles: [],
                },
            ];
        }

        this.projectTeamsByDate.set(projectId, byDate);
    }

    private async loadTeamsSnapshotForProject(
        projectId: number,
        from: string,
        to: string,
        days: string[]
    ) {
        try {
            // Fetch persisted team/vehicle assignments for this project window.
            const resp = await firstValueFrom(
                this.cap.getProjectGroupsSnapshot(projectId, from, to)
            );
            const teamDays = resp?.team_days ?? [];
            const vehicles = resp?.vehicles ?? [];

            const byTeamDay: Record<number, any[]> = {};
            for (const v of vehicles) {
                if (v.vehicle_id) {
                    (byTeamDay[v.team_day_id] ||= []).push(v);
                }
            }

            const nextByDate: Record<string, TeamUnit[]> = {};

            for (const td of teamDays) {
                // Reconstruct team units from snapshot rows.
                const unit: TeamUnit = {
                    teamId: td.team_id,
                    kind: td.team_kind as any,
                    teamName: td.team_name,
                    vehicles: (byTeamDay[td.team_day_id] ?? []).map((v) => ({
                        id: v.vehicle_id,
                        name: v.vehicle_name ?? undefined,
                        plate: v.plate ?? undefined,
                        is_locked: !!v.is_locked,
                        notes: v.notes ?? undefined,
                        code: String(v.vehicle_id),
                    })),
                    totalHours: td.total_hours ?? undefined,
                    locked: !!td.locked,
                    members: [],
                };

                (nextByDate[td.day] ||= []).push(unit);
            }

            const defaultName = this.projectTeamName();
            for (const d of days) {
                const arr = nextByDate[d] ?? [];
                if (!arr.some((t) => t.kind === 'PROJECT_DAY')) {
                    // Ensure the add-vehicle UI is always available.
                    arr.push({
                        teamId: 1,
                        kind: 'PROJECT_DAY',
                        teamName: defaultName,
                        vehicles: [],
                    });
                }
                nextByDate[d] = arr;
            }

            this.projectTeamsByDate.set(projectId, nextByDate);
            // Ensure Teams row updates immediately in OnPush mode.
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
            this.bumpTeamsResolver();
        } catch (e) {
            // Keep UI usable when snapshot fails.
            const fallback: Record<string, TeamUnit[]> = {};
            const defaultName = this.projectTeamName();
            for (const d of days) {
                fallback[d] = [
                    {
                        teamId: 1,
                        kind: 'PROJECT_DAY',
                        teamName: defaultName,
                        vehicles: [],
                    },
                ];
            }
            this.projectTeamsByDate.set(projectId, fallback);
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
            this.bumpTeamsResolver();
        }
    }

    /**
     * Clear transient dirty flags for the given project/day set after a successful save.
     */
    private clearDirtyFlagsForDates(projectId: number, dates: string[]) {
        const byDate = this.projectTeamsByDate.get(projectId) ?? {};
        const next: Record<string, TeamUnit[]> = { ...byDate };

        for (const d of dates) {
            const teams = next[d];
            if (!teams) continue;
            next[d] = teams.map((t) => ({ ...t, dirty: false }));
        }

        this.projectTeamsByDate.set(projectId, next);
        this.rows.set([...this.rows()]);
        this.bumpTeamsResolver();
    }

    /**
     * Persist vehicle changes immediately for a project (optionally limited to specific days),
     * so the UI reflects the current state without a full page reload.
     */
    private async persistProjectVehicles(
        projectId: number,
        onlyDates?: string[]
    ) {
        const normDates = onlyDates?.map((d) => d.slice(0, 10));
        const payload = this.buildTeamsPayloadForProject(
            projectId,
            true,
            normDates
        );

        if (!payload.length) {
            this.clearProjectDirty(projectId);
            return;
        }

        this.silentProgress = true;

        try {
            await firstValueFrom(
                this.cap.applyProjectGroups(projectId, payload, true, false)
            );

            if (normDates?.length) {
                this.clearDirtyFlagsForDates(projectId, normDates);
            }
            this.clearProjectDirty(projectId);
            this.bumpTeamsResolver();
            this.rows.set([...this.rows()]);
        } catch (e) {
            console.error('Failed to persist vehicle changes:', e);
            this.toastr.error(
                this.transloco.translate('FAILED_TO_SAVE_VEHICLES')
            );
        } finally {
            this.silentProgress = false;
            this._changeDetectorRef.markForCheck();
        }
    }

    private collectProjectIdsForTeams(): number[] {
        const ids = new Set<number>();
        for (const r of this.rows()) {
            if (r.kind === 'PROJECT' && r.meta?.projectId != null) {
                ids.add(Number(r.meta.projectId));
            }
        }
        return Array.from(ids.values());
    }

    private async loadTeamsForProjects(from: string, to: string) {
        if (this.viewMode() !== 'PROJECT') return;

        const projectIds = this.collectProjectIdsForTeams();
        if (!projectIds.length) return;

        const days = this.dates();

        // Reset in-memory teams and pre-seed each day with a default team.
        this.projectTeamsByDate.clear();
        this.dirtyTeamProjects.clear();
        this.teamsDirty = false;

        for (const projectId of projectIds) {
            this.initProjectTeams(projectId, days);
        }

        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();

        await Promise.all(
            projectIds.map((projectId) =>
                this.loadTeamsSnapshotForProject(projectId, from, to, days)
            )
        );

        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
        this.bumpTeamsResolver();
    }

    private buildTeamsPayloadForProject(
        projectId: number,
        sendEmptyTeams = false,
        onlyDates?: string[]
    ): ProjectGroupDayDTO[] {
        const byDate = this.projectTeamsByDate.get(projectId) ?? {};
        const out: ProjectGroupDayDTO[] = [];
        const allDays = Object.keys(byDate).sort();
        const allowed = onlyDates
            ? new Set(onlyDates.map((d) => d.slice(0, 10)))
            : null;

        for (const d of allDays) {
            if (allowed && !allowed.has(d)) continue;

            // Skip clean/empty days unless explicit full sync is requested.
            const units = (byDate[d] ?? []).filter((t) =>
                sendEmptyTeams
                    ? true
                    : t?.dirty || (t?.vehicles?.length ?? 0) > 0
            );

            for (const t of units) {
                out.push({
                    date: d,
                    team_id: t.teamId,
                    team_kind: t.kind,
                    team_name: t.teamName,
                    locked: !!t.locked,
                    total_hours: t.totalHours,
                    vehicles: (t.vehicles ?? []).map((v) => ({
                        vehicle_id: v.id,
                        is_locked: !!v.is_locked,
                        notes: v.notes ?? '',
                    })),
                });
            }
        }

        return out;
    }

    /** Ensure the teams summary row exists at the end of rows() */
    private ensureTeamsSummaryRow(list: Row[]) {
        if (!list.some((r) => r.resourceKey === this.TEAM_SUMMARY_KEY)) {
            list.push({
                resourceKey: 'DIVIDER_TEAMS',
                resourceName: '',
                kind: 'DIVIDER',
                class: '', // po želji
            } as Row);

            list.push({
                resourceKey: this.TEAM_SUMMARY_KEY,
                resourceName: 'TEAMS',
                kind: 'TEAMS',
            } as Row);
        }
    }

    /** Build a quick lookup of employee per-day hours from the already aggregated employee bars */
    private buildEmployeeHoursIndex(
        employeeRows: Row[]
    ): Map<
        string,
        Array<{ resourceKey: string; resourceName: string; hours: number }>
    > {
        const byDate = new Map<
            string,
            Array<{ resourceKey: string; resourceName: string; hours: number }>
        >();
        const allDates = this.dates();

        for (const r of employeeRows) {
            if (r.kind !== 'EMPLOYEE') continue;
            // 1-day bars with total_hours (as built by aggregateEmployeeBars)
            const bars = Array.isArray(r.bars) ? r.bars : [];
            // Create a quick map date -> hours for this employee
            const perDay = new Map<string, number>();
            for (const b of bars) {
                // bars are day-long with end exclusive; get start day
                const day = b.start_date.slice(0, 10);
                const h = Math.max(
                    0,
                    Math.round((b.total_hours ?? 0) * 100) / 100
                );
                if (h > 0) perDay.set(day, (perDay.get(day) ?? 0) + h);
            }

            // Fill the global index
            for (const d of allDates) {
                const h = perDay.get(d) ?? 0;
                if (h <= 0) continue;
                const arr = byDate.get(d) ?? [];
                arr.push({
                    resourceKey: r.resourceKey,
                    resourceName: r.resourceName,
                    hours: h,
                });
                byDate.set(d, arr);
            }
        }

        // Sort members by hours desc for each date to get stable grouping
        for (const [d, arr] of byDate.entries()) {
            arr.sort(
                (a, b) =>
                    b.hours - a.hours ||
                    a.resourceName.localeCompare(b.resourceName)
            );
        }

        return byDate;
    }

    /** Demo grouping: create up to two teams per day from members with >0h, assign demo vehicles */
    private buildDemoTeams(
        byDate: Map<
            string,
            Array<{ resourceKey: string; resourceName: string; hours: number }>
        >
    ) {
        this.teamsByDate.clear();

        let nextId = 1;
        const mkTeam = (
            name: string,
            members: Array<{
                resourceKey: string;
                resourceName: string;
                hours: number;
            }>,
            vehicles: string[],
            locked = false
        ) => {
            const total = members.reduce((s, m) => s + (m.hours || 0), 0);
            return {
                teamId: nextId++,
                teamName: name,
                totalHours: Math.round(total * 100) / 100,
                locked,
                vehicles: vehicles.map((code) => ({ code })),
                members,
            };
        };

        for (const [date, members] of byDate.entries()) {
            if (!members.length) continue;

            // Split members into 1–2 groups to visualize multiple teams
            const g1 = members.slice(0, Math.ceil(members.length / 2));
            const g2 = members.slice(Math.ceil(members.length / 2));

            const teams: any[] = [];
            teams.push(mkTeam('ALPHA', g1, ['V-03']));
            if (g2.length) teams.push(mkTeam('BETA', g2, ['V-12']));

            // Example: if only one member with a lot of hours, show locked DISPATCH
            if (members.length === 1 && members[0].hours >= 8) {
                teams.length = 0;
                teams.push(mkTeam('DISPATCH', members, ['V-22'], true));
            }

            this.teamsByDate.set(date, teams);
        }
    }

    /** Method for retrieving holidays and setting the holidays dict */
    async getHolidays(from: Date | null, to: Date | null) {
        const holidaysResp = await firstValueFrom(
            this._holidaysService.getHolidays(
                /* page   */ 0,
                /* size   */ 0,
                /* sort   */ 'date',
                /* order  */ 'asc',
                /* search */ '',
                /* filter */ {
                    year: null,
                    use_pagination: 0,
                    from: from,
                    to: to,
                }
            )
        );

        const holidays: any[] = holidaysResp?.holidays ?? [];

        for (const h of holidays) {
            const ymd = this.utils.toYMD(h.date);
            (this.holidayDict[ymd] ||= []).push(h);
        }

        console.log('Loaded holidays:', this.holidayDict);
    }
    /**
     * Fetch absences. Window (winFrom/winTo) is optional:
     * - If dates()/rows() are not ready yet → we still fetch and fill the cache.
     * - We then (re)build the per-day index with/without window.
     *   If window is missing, periods are expanded fully (watch dataset size).
     */
    async getUserAbsences(from: Date | null, to: Date | null): Promise<void> {
        // 1) fetch raw absences (pure; returns array)
        const abs = await this._absencesLeaveService.fetchAbsences(from, to, {
            excludeRejected: true,
        });

        // 2) group into your component-owned map
        this._absencesLeaveService.groupAbsencesByUser(
            abs,
            this.absencesByUser
        );

        // 3) build your component-owned per-day index for your window
        this._absencesLeaveService.buildUserDayIndexInto(
            this.absencesByUser,
            this.absenceDayIndex,
            from,
            to
        );
    }

    /** Resolver passed to the timeline (row+date → CellMark[]) */
    absencesForRowDate = (row: Row, d: string | Date): CellMark[] => {
        const m = row.resourceKey.match(/(?:^|[\|])USER:(\d+)/);
        const uid = m ? Number(m[1]) : undefined;
        if (!uid) return [];
        const perDay = this.absenceDayIndex.get(uid);
        const key = this.utils.toYMD(d); // LOCAL key — matches builder
        return perDay?.[key] || [];
    };

    /**
     * Switches between EMPLOYEE-first and PROJECT-first grouping.
     * Does not refetch data; it rebuilds rows from cached raw response.
     */
    async setViewMode(mode: ViewMode): Promise<void> {
        this.viewMode.set(mode);

        // Detect changes
        this._changeDetectorRef.detectChanges();

        // Rebuild rows if we have a date range cached (raw may be empty and that's OK)
        if (this._lastFrom && this._lastTo) {
            this.rows.set(
                this.buildRowsForView(
                    this._lastRaw ?? [],
                    this._lastFrom,
                    this._lastTo
                )
            );
            this.buildUnassignedEmployeesIndex(
                this._lastRaw ?? [],
                this._lastFrom,
                this._lastTo
            ); // prepare unassigned employees index

            if (mode === 'PROJECT') {
                // Refresh per-project vehicle teams when switching views.
                await this.loadTeamsForProjects(this._lastFrom, this._lastTo);
            }
            this.silentProgress = false;

            console.log('[this.rows] out:', this.rows());
        }
    }

    /**
     * Converts raw API bars to UI rows depending on selected grouping view.
     */
    private buildRowsForView(
        raw: MixedRawBar[],
        from: string,
        to: string
    ): Row[] {
        console.log('[buildRowsForView]', {
            raw,
            from,
            to,
            viewMode: this.viewMode(),
        });

        // Filter out rows to not include GLOBAL_ASSIGNMENT row_kind of row
        raw = raw.filter((r) => r.row_kind !== 'GLOBAL_ASSIGNMENT');

        return this.viewMode() === 'EMPLOYEE'
            ? this.buildEmployeeFirstRows(raw, from, to)
            : this.buildProjectFirstRows(raw, from, to);
    }

    /**
     * EMPLOYEE view (current):
     * Parent rows are USERS, children rows are PROJECTS per user.
     *
     * NOTE: resourceKey for a user MUST be `USER:<id>` (not EMPLOYEE:<id>)
     * because raw data is indexed by resource_type/resource_id.
     */
    private buildEmployeeFirstRows(
        raw: MixedRawBar[],
        from: string,
        to: string
    ): Row[] {
        // Index raw rows by `USER:<id>` for quick lookup
        const byKey = new Map<string, MixedRawBar[]>();
        for (const r of raw) {
            const key = `${r.resource_type}:${r.resource_id}`; // ex: USER:45
            if (!byKey.has(key)) byKey.set(key, []);
            byKey.get(key)!.push(r);
        }

        let out: Row[] = [];

        // Keep your current ordering: iterate your loaded users list
        for (const u of this.users) {
            const parentKey = `USER:${u.id}`;
            const rawForUser = byKey.get(parentKey) ?? [];

            // Parent row: aggregated per-day total for this user across all projects
            out.push({
                resourceKey: parentKey,
                resourceName: `${u.first_name} ${u.last_name}`,
                kind: 'EMPLOYEE',
                class: 'bg-amber-50',
                bars: this.aggregateEmployeeBars(rawForUser, from, to),
            });

            // Child rows: group that user's raw bars by project
            const byProject = this.groupByProject(rawForUser);
            for (const p of byProject) {
                out.push({
                    resourceKey: `${parentKey}|PROJECT:${p.project_id}`,
                    resourceName: `${p.project_number}`,
                    kind: 'PROJECT',
                    class: '!pl-6 italic bg-white',
                    bars: p.bars,
                });
            }
        }

        out = this.ensureUnassignedEmployeesRow(out);
        return out;
    }

    /**
     * PROJECTS → EMPLOYEES view:
     * Parent rows are PROJECTS, child rows are USERS working on that project.
     */
    // private buildProjectFirstRows(
    //     raw: MixedRawBar[],
    //     from: string,
    //     to: string
    // ): Row[] {
    //     // Group raw bars by project_id (may be empty)
    //     const byProjectId = new Map<number, MixedRawBar[]>();
    //     for (const r of raw ?? []) {
    //         if (!byProjectId.has(r.project_id))
    //             byProjectId.set(r.project_id, []);
    //         byProjectId.get(r.project_id)!.push(r);
    //     }
    //
    //     // We ALWAYS want to list projects (even if raw is empty)
    //     const projectIds: number[] =
    //         Array.isArray(this.projects) && this.projects.length
    //             ? this.projects.map((p: any) => Number(p.id))
    //             : Array.from(byProjectId.keys()); // fallback if projects list is not loaded
    //
    //     let out: Row[] = [];
    //
    //     for (const projectId of projectIds) {
    //         const rawForProject = byProjectId.get(projectId) ?? [];
    //         const parentKey = `PROJECT:${projectId}`;
    //
    //         // Parent project row exists even if rawForProject is empty
    //         out.push({
    //             resourceKey: parentKey,
    //             resourceName: this.getProjectLabel(projectId, rawForProject),
    //             kind: 'PROJECT',
    //             class: '',
    //             bars: this.aggregateProjectBars(rawForProject, from, to), // will be [] if rawForProject is []
    //         });
    //
    //         // If there is no raw data, there are no employees to show under the project
    //         if (!rawForProject.length) continue;
    //
    //         // Group project raw bars by user key (USER:<id>)
    //         const byUserKey = new Map<string, MixedRawBar[]>();
    //         for (const r of rawForProject) {
    //             const userKey = `${r.resource_type}:${r.resource_id}`;
    //             if (!byUserKey.has(userKey)) byUserKey.set(userKey, []);
    //             byUserKey.get(userKey)!.push(r);
    //         }
    //
    //         // Add only employees who have allocations on this project
    //         for (const u of this.users ?? []) {
    //             const userKey = `USER:${u.id}`;
    //             const rawForUserOnProject = byUserKey.get(userKey) ?? [];
    //             if (!rawForUserOnProject.length) continue;
    //
    //             out.push({
    //                 resourceKey: `${parentKey}|${userKey}`,
    //                 resourceName: `${u.first_name} ${u.last_name}`,
    //                 kind: 'EMPLOYEE',
    //                 class: '!pl-6 italic',
    //                 meta: {
    //                     projectId
    //                 },
    //                 bars: this.toBars(rawForUserOnProject),
    //             });
    //         }
    //     }
    //
    //     out = this.ensureUnassignedEmployeesRow(out);
    //     return out;
    // }

    /**
     * V2 PROJECTS → EMPLOYEES view:
     * Parent rows are PROJECTS, child rows are USERS working on that project.
     */
    private buildProjectFirstRows(
        raw: MixedRawBar[],
        from: string,
        to: string
    ): Row[] {
        const usesWorkItems = this.planningPermissions.usesWorkItems;
        const usesSquareMeters = this.planningPermissions.usesSquareMeters;

        const out: Row[] = [];

        for (const project of this.projects ?? []) {
            const projectId = Number(project.id);
            const projectKey = `PROJECT:${projectId}`;

            const projectRaw = this.indexByProject.get(projectId) ?? [];

            // -------------------------------
            // PROJECT ROW
            // -------------------------------
            const rowActions =
                this.viewMode() === 'PROJECT'
                    ? ([
                          {
                              id: 'ADD_RESOURCE',
                              labelKey: 'ADD_RESOURCE_TO_PROJECT',
                              icon: 'heroicons_outline:plus',
                          },
                          ...(usesWorkItems
                              ? [
                                    {
                                        id: 'ADD_WORK_TYPE',
                                        labelKey: 'ADD_WORK_ITEM_TO_PROJECT',
                                        icon: 'heroicons_outline:wrench',
                                    },
                                ]
                              : []),
                      ] as RowAction[])
                    : undefined;

            out.push({
                resourceKey: projectKey,
                resourceName: project.project_number ?? `Project #${projectId}`,
                kind: 'PROJECT',
                meta: { projectId },
                rowActions,
                bars: this.aggregateProjectBars(projectRaw, from, to),
            });

            // -------------------------------
            // WORK ITEMS (only when allowed)
            // -------------------------------
            const projectResourcePool =
                this.collectProjectWorkItemResources(projectId);

            const workItemIds = new Set<number>();
            for (const r of projectRaw) {
                workItemIds.add(r.work_item_id ?? 0);
            }

            for (const workItemId of workItemIds) {
                const pwKey = `${projectId}|${workItemId}`;
                const workItemRaw =
                    this.indexByProjectWorkItem.get(pwKey) ?? [];

                if (!workItemRaw.length) continue;

                const workTypeName =
                    workItemRaw[0].work_type_name ??
                    workItemRaw[0].work_item_name ??
                    null;
                const wiLabel =
                    workItemRaw[0].work_item_name ??
                    workItemRaw[0].work_type_name ??
                    'GENERAL';

                let workTypeId = workItemRaw[0].work_type_id ?? 0;

                const workItemKey = `${projectKey}|WORK_ITEM:${workItemId}`;

                // -------------------------------
                // WORK ITEM ROW
                // -------------------------------
                if (usesWorkItems) {
                    out.push({
                        resourceKey: workItemKey,
                        resourceName: wiLabel,
                        kind: 'WORK_ITEM',
                        class: `${this.workTypeBg(
                            workTypeId,
                            '50'
                        )} !pl-4 font-semibold`,
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            workTypeName,
                        },
                        bars: [], // this.aggregateProjectBars(workItemRaw, from, to),
                    });
                }

                // -------------------------------
                // EMPLOYEES UNDER WORK ITEM
                // -------------------------------
                const byUser = new Map<string, MixedRawBar[]>();
                for (const r of workItemRaw) {
                    if (!this.isValidResourceRef(r)) continue;
                    const uKey = `${r.resource_type}:${r.resource_id}`;
                    if (!byUser.has(uKey)) byUser.set(uKey, []);
                    byUser.get(uKey)!.push(r);
                }
                // include every resource seen on any work item for this project
                for (const [uKey] of projectResourcePool) {
                    if (!byUser.has(uKey)) byUser.set(uKey, []);
                }

                for (const [userKey, slice] of byUser.entries()) {
                    const [resource_type, resource_id_str] = userKey.split(':');
                    const resource_id = Number(resource_id_str);
                    const u = this.users.find(
                        (x) => Number(x.id) === resource_id
                    );
                    const poolEntry = projectResourcePool.get(userKey);
                    const displayName = this.formatResourceName({
                        first_name: u?.first_name,
                        last_name: u?.last_name,
                        resource_name: poolEntry?.resourceName,
                        resource_type,
                        resource_id,
                    });
                    if (!displayName) continue;

                    out.push({
                        resourceKey: usesWorkItems
                            ? `${workItemKey}|${userKey}`
                            : `${projectKey}|${userKey}`,
                        resourceName: displayName,
                        kind: 'EMPLOYEE',
                        class: usesWorkItems ? '!pl-8 italic' : '!pl-4 italic',
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            workTypeName,
                        },
                        bars: this.toBars(slice),
                    });
                }

                // -------------------------------
                // PROJECT DAY m² SPECIAL ROW
                // -------------------------------
                if (
                    usesSquareMeters &&
                    !this.filterObject.employee_ids?.length
                ) {
                    out.push({
                        resourceKey: `${workItemKey}|PROJECT_DAY_M2`,
                        resourceName: 'm² / dan',
                        kind: 'SPECIAL',
                        class: `!pl-8 ${this.workTypeBg(
                            workTypeId,
                            '100'
                        )} font-semibold`,
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            workTypeName,
                            type: 'PROJECT_DAY_M2',
                        },
                        bars: this.buildProjectDayM2Bars(
                            projectId,
                            workItemId,
                            from,
                            to
                        ),
                    });
                }

                // -------------------------------
                // PROJECT DAY NOTES SPECIAL ROW
                // -------------------------------
                if (this.planningPermissions.allowProjectDayNotes) {
                    out.push({
                        resourceKey: `${workItemKey}|PROJECT_DAY_NOTES`,
                        resourceName: this.transloco.translate('NOTES'),
                        kind: 'SPECIAL',
                        class: `!pl-8 ${this.workTypeBg(
                            workTypeId,
                            '200'
                        )} font-semibold`,
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            type: 'PROJECT_DAY_NOTES',
                        },
                        bars: this.buildProjectDayNotesBars(
                            projectId,
                            workItemId,
                            from,
                            to
                        ),
                    });
                }
            }

            // -------------------------------
            // VEHICLES (PROJECT DAY) ROW
            // -------------------------------
            out.push({
                resourceKey: `${projectKey}|TEAMS`,
                resourceName: this.transloco.translate('VEHICLES_LABEL'),
                kind: 'TEAMS',
                class: '!pl-4 font-semibold text-slate-600',
                meta: { projectId },
                bars: [],
            });

            out.push({
                resourceKey: 'DIVIDER_TEAMS',
                resourceName: '',
                kind: 'DIVIDER',
                class: '',
            } as Row);
        }

        return this.ensureUnassignedEmployeesRow(out);
    }

    /**
     * Build 1-day bars for PROJECT_DAY_M2 special row.
     * One bar per day, spanning exactly one date column.
     *
     * Bars are created even if value is null,
     * so the cell remains clickable/editable.
     */
    private buildProjectDayM2Bars(
        projectId: number,
        workItemId: number,
        from: string,
        to: string
    ): Bar[] {
        const bars: Bar[] = [];

        // Iterate only visible timeline dates (fast, bounded)
        for (const day of this.dates()) {
            if (day < from || day > to) continue;

            const key = `${projectId}|${workItemId ?? 0}|${day}`;
            const value = this.indexProjectDayM2.get(key) ?? null;

            bars.push({
                start_date: day,
                end_date: this.nextDay(day), // exclusive end
                square_meters: value, // may be null
                total_hours: null, // IMPORTANT: do not affect hours logic
                allocation_percent: null,
                is_manual: true,
                is_locked: false,
            });
        }

        return bars;
    }

    /**
     * Build 1-day bars for PROJECT_DAY_NOTES special row.
     * Bars are created even if value is null to keep cells clickable.
     */
    private buildProjectDayNotesBars(
        projectId: number,
        workItemId: number,
        from: string,
        to: string
    ): Bar[] {
        const bars: Bar[] = [];

        for (const day of this.dates()) {
            if (day < from || day > to) continue;

            const key = `${projectId}|${workItemId ?? 0}|${day}`;
            const value = this.indexProjectDayNotes.get(key) ?? null;

            bars.push({
                start_date: day,
                end_date: this.nextDay(day),
                total_hours: null,
                square_meters: null,
                allocation_percent: null,
                is_manual: true,
                is_locked: false,
                label: value ?? undefined,
            } as any);
        }

        return bars;
    }

    /**
     * Aggregates all allocations for a project into per-day bars.
     * We can reuse the same logic used for employee totals, because both are:
     * "sum allocations across a slice of raw bars per day".
     */
    private aggregateProjectBars(
        rawForProject: MixedRawBar[],
        from: string,
        to: string
    ): Bar[] {
        return this.aggregateEmployeeBars(rawForProject, from, to);
    }

    /**
     * Converts raw backend records (MixedRawBar) into UI bars (Bar).
     * IMPORTANT: Bar uses start_date/end_date (end is exclusive in your UI).
     */
    private toBars(rawSlice: MixedRawBar[]): Bar[] {
        return (rawSlice ?? []).map((r) => ({
            start_date: r.start_date,
            end_date: r.end_date,
            total_hours: r.total_hours ?? null,
            square_meters: r.square_meters ?? null,
            allocation_percent: r.allocation_percent ?? null,
            is_manual: Boolean(r.is_manual),
            is_locked: Boolean(r.is_locked),
            resource_name: r.resource_name ?? undefined,

            // Optional: you can style bars based on lock/manual flags
            // cssClass: r.is_locked ? 'opacity-70' : '',
        }));
    }

    /**
     * Returns a safe project label for UI (prefer project_number, then name, then fallback).
     * Works even if `this.projects` does not include the project id.
     */
    private getProjectLabel(
        projectId: number,
        rawForProject: MixedRawBar[]
    ): string {
        // 1) Prefer data from loaded projects list (stable naming)
        const p = this.projects?.find(
            (x: any) => Number(x?.id) === Number(projectId)
        );
        if (p) {
            // Use whatever your real project object contains. Adjust if needed.
            return p.project_number ?? `Project #${projectId}`;
        }

        // 2) Fall back to what we got from backend raw data
        const fromRaw = rawForProject?.[0];
        const rawNumber = fromRaw?.project_number ?? '';
        const rawName = fromRaw?.project_name ?? '';
        if (rawNumber && rawName) return `${rawNumber} — ${rawName}`;
        if (rawNumber) return rawNumber;

        // 3) Last resort
        return `Project #${projectId}`;
    }

    /**
     * Calculates which day inside a multi-day bar was clicked,
     * based on mouse X position relative to the bar width.
     *
     * @param evt Mouse click event
     * @param bar Allocation bar
     * @returns YYYY-MM-DD string representing the clicked day
     */
    dateFromBarClick(evt: MouseEvent, bar: any): string {
        // HTMLElement representing the bar
        const target = evt.currentTarget as HTMLElement;

        // Get bar position and width in the viewport
        const rect = target.getBoundingClientRect();

        // Horizontal offset of click inside the bar
        const x = evt.clientX - rect.left;

        // Total bar width in pixels
        const width = rect.width;

        // Number of days spanned by the bar (columns)
        const totalDays = this.spanCols(bar.start_date, bar.end_date) ?? 1;

        // Determine which day index was clicked (0-based)
        const dayIndex = Math.min(
            totalDays - 1,
            Math.max(0, Math.floor((x / width) * totalDays))
        );

        // Compute actual date by offsetting from bar start
        const d = new Date(bar.start_date);
        d.setDate(d.getDate() + dayIndex);

        // Return normalized YYYY-MM-DD string
        return d.toISOString().slice(0, 10);
    }

    /**
     * Returns the number of hours allocated for a specific day
     * inside a bar.
     *
     * - Single-day bar → return total hours
     * - Multi-day bar → evenly distribute total hours across days
     *
     * @param bar Allocation bar
     * @param day YYYY-MM-DD (not strictly required but kept for clarity)
     */
    perDayHoursFromBar(bar: any, day: string): number {
        // If the bar already represents a single day
        if (bar.start_date === bar.end_date) {
            return bar.total_hours ?? 0;
        }

        // Multi-day bar: compute number of days
        const start = new Date(bar.start_date);
        const end = new Date(bar.end_date);

        const days = Math.max(
            1,
            Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
        );

        // Evenly distribute hours across days
        return Math.round((bar.total_hours ?? 0) / days);
    }

    /**
     * Returns the number of square meters allocated for a specific day
     * inside a bar.
     *
     * Rules:
     * - Single-day bar ([d, d+1)) → return total square meters
     * - Multi-day bar ([start, end)) → evenly distribute total square meters
     *
     * IMPORTANT:
     * - end_date is EXCLUSIVE everywhere in the timeline
     * - This matches perDayHoursFromBar logic exactly
     *
     * @param bar Allocation bar
     * @param day YYYY-MM-DD (kept for symmetry, not strictly required)
     */
    perDaySquareMetersFromBar(bar: any, day: string): number {
        const total = Number(bar.square_meters ?? 0);

        // No square meters → fast exit
        if (total <= 0) return 0;

        // Calculate number of days covered by the bar (UTC-safe)
        const start = new Date(bar.start_date);
        const end = new Date(bar.end_date);

        const days = Math.max(
            1,
            Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))
        );

        // Even distribution across days
        return Math.round((total / days) * 100) / 100;
    }

    /**
     * Handles left-click on an allocation bar.
     * Determines clicked day and opens the day editor dialog
     * with correct initial hours.
     *
     * @param evt Mouse click event
     * @param bar Allocation bar
     * @param row Timeline row (resource)
     */
    editBarAtPointer(evt: MouseEvent, bar: any, row: Row): void {
        console.log('[editBarAtPointer]', { evt, bar, row });

        // Prevent click from bubbling to cell handlers
        evt.stopPropagation();

        // Determine which day inside the bar was clicked
        const day = this.dateFromBarClick(evt, bar);

        // Calculate initial hours for that specific day
        const initialHours = this.perDayHoursFromBar(bar, day);
        const initialSquareMeters = this.perDaySquareMetersFromBar(bar, day);

        // Open existing Add Resource dialog in "day" mode
        this.openAddResourceDialog(
            'day',
            day,
            row,
            initialHours,
            initialSquareMeters
        );
    }

    /** Returns true if the provided date (YYYY-MM-DD) is strictly before today. */
    isPast(d: string): boolean {
        // String comparison is safe in YYYY-MM-DD format
        return false; // d < this.todayISO();
    }

    /** Weekend detector used by timeline header */
    isWeekendDay = (d: string | Date): boolean => {
        const dt = new Date(d);
        const day = dt.getDay();
        return day === 0 || day === 6;
    };

    /**
     * Open "Add Resource" dialog in either 'global' or 'day' mode.
     * @param mode
     * @param date
     * @param row
     * @param initialHours
     * @param initialSquareMeters
     */
    async openAddResourceDialog(
        mode: 'global' | 'day',
        date?: string,
        row?: Row,
        initialHours: number = 0,
        initialSquareMeters: number = 0
    ) {
        console.log('Row: ', row);
        console.log('Date: ', date);
        console.log('Initial hours: ', initialHours);
        console.log('Initial square meters: ', initialSquareMeters);

        if (this.silentProgress || this.isLoading) {
            this.toastr.info(
                this.transloco.translate('PLEASE_WAIT_LOADING_COMPLETES')
            );
            return;
        }

        if (this.disablePastEditing && this.isPast(date)) {
            this.toastr.warning(
                this.transloco.translate('PAST_DATE_DISABLED_FOR_UTILIZATION')
            );
            return;
        }

        // Project day square meters edit
        if (row.meta?.type === 'PROJECT_DAY_M2') {
            this.openProjectDaySquareMetersDialog(
                row.meta.projectId,
                row.meta.workItemId ?? null,
                date,
                row.meta.workTypeId ?? null,
                row.meta.workTypeName ?? row.resourceName ?? null
            );
            return;
        }

        // Project day notes edit
        if (row.meta?.type === 'PROJECT_DAY_NOTES') {
            if (!this.planningPermissions.canAddNotes) {
                return;
            }
            this.openProjectDayNotesDialog(
                row.meta.projectId,
                row.meta.workItemId ?? null,
                date
            );
            return;
        }

        let maxHours = 24;

        // zgradi exclude seznam iz trenutnih vrstic v Gantt-u
        const exclude: ResourceRef[] = (this.rows() || []).map((r) => {
            const [rt, id] = r.resourceKey.split(':');
            return {
                resource_type: rt.toUpperCase() as any,
                resource_id: Number(id),
                resource_name: r.resourceName,
            };
        });

        console.log(
            'Opening add resource dialog with mode:',
            mode,
            'date:',
            date,
            'row:',
            row,
            'initialHours:',
            initialHours,
            'exclude:',
            exclude
        );

        let projectId = row.meta.projectId || null;
        const parsed = this.capacityService.parseResourceFromRow(row);
        const { resource_type, resource_id } = parsed;

        try {
            this.silentProgress = true;

            if (mode === 'day' && date && row) {
                if (!parsed) {
                    console.warn('No resource found in row:', row);
                    return;
                }

                // retrieve available utilization for that day and resource
                let days = await firstValueFrom(
                    this.cap.getRemainingCapacity(
                        resource_type,
                        +resource_id,
                        projectId,
                        date
                    )
                );
                maxHours = days[0]?.remaining_hours ?? 0;
            }
        } catch (e) {
            console.error('Error preparing Add Resource dialog:', e);
            this.silentProgress = false;
        }
        this.silentProgress = false;

        const data: AddResourceDialogData = {
            projectId,
            mode,
            preselect: row
                ? {
                      resource_type,
                      resource_id,
                      resource_name: row.resourceName,
                  }
                : undefined,
            date: date ?? null,
            lockDate: mode === 'day',
            initialHours,
            initialSquareMeters,
            exclude,
            maxHours,
        };

        console.log('Dialog data:', data);

        this.dialog
            .open(AddResourceDialogComponent, { width: '520px', data })
            .afterClosed()
            .subscribe(async (res?: AddResourceDialogResult) => {
                if (!res) return;
                if (res.deleted && row && date) {
                    // Delete instead of upsert
                    await firstValueFrom(
                        this.cap.deleteWorkItemDayManual(
                            row.meta?.projectId ?? null,
                            {
                                work_item_id: row.meta?.workItemId ?? null,
                                resource_type:
                                    res.resource?.resource_type ?? null,
                                resource_id: res.resource?.resource_id ?? null,
                                date,
                            }
                        )
                    );
                    await this.load();
                    return;
                }

                const from =
                    this.from ??
                    this.dates()[0] ??
                    new Date().toISOString().slice(0, 10);
                const to =
                    this.to ?? this.dates()[this.dates().length - 1] ?? from;

                // TODO: Here in our first case for updating specific existing resource we do need to update per day and resource and project its hours plan
                // TODO: We shall added as well number of squares field for planning next to hours

                // ----- CASE A: Global add (dodaj vir brez datuma) -----
                // if (res.resource && !res.date && !res.from && !res.to) {
                //     const key = `${res.resource.resource_type}:${res.resource.resource_id}`;
                //
                //     // 1) add row if not exists yet, but after the potential project phases rows
                //     if (!this.rows().some((r) => r.resourceKey === key)) {
                //         // add after project phases
                //         let lastPhaseIndex = this.rows().findIndex(
                //             (r) => r.kind !== 'PHASE'
                //         );
                //         if (lastPhaseIndex === -1)
                //             lastPhaseIndex = this.rows().length;
                //
                //         const next = this.rows().slice();
                //         next.splice(lastPhaseIndex, 0, {
                //             resourceKey: key,
                //             resourceName: res.resource.resource_name,
                //             bars: [],
                //         });
                //
                //         this.rows.set(next);
                //     }
                //
                //     // 2) zgradi resources iz trenutnih vrstic (vključno z novim virom)
                //     const resources = this.buildResourcesFromRows();
                //
                //     // 3) manual locks iz pending (če obstajajo)
                //     const manualLocks = this.buildManualLocksFromPending();
                //
                //     // 4) time window
                //     const today = this.todayISO(); // English: local YYYY-MM-DD (not UTC)
                //     const from = this.from ?? this.dates()?.[0] ?? today;
                //     const to =
                //         this.to ??
                //         this.dates()?.[this.dates().length - 1] ??
                //         from;
                //
                //     // never start before today
                //     const effFrom = from < today ? today : from;
                //
                //     // guard against effFrom > to (empty window). If so, clamp "to" to effFrom.
                //     const effTo = to < effFrom ? effFrom : to;
                //
                //     if (!this.autoSuggestOnAdd()) {
                //         // English: row added, no auto suggestion executed
                //         // this.toastr.info(this.transloco.translate('AUTO_SUGGEST_ON_ADD_DISABLED_INFO'));
                //         this.dirty = true; // allow manual suggest/apply later
                //         return;
                //     }
                //
                //     // 5) weighted call + unified UI apply
                //     this.silentProgress = true;
                //     try {
                //         await this.runWeightedAndApply({
                //             from: effFrom, // <<< use effFrom instead of from
                //             to: effTo, // <<< use clamped "to" to avoid empty range
                //             resources,
                //             manualLocks,
                //             replaceProjectLoad: true,
                //             countryId: this.countryId ?? null,
                //         });
                //     } finally {
                //         this.silentProgress = false;
                //     }
                //
                //     return;
                // }

                // ----- CASE B: Single day (weighted rescheduling, REBALANCE, with locks) -----
                if (
                    res.resource &&
                    res.date &&
                    (typeof res.hours === 'number' ||
                        typeof res.square_meters === 'number')
                ) {
                    try {
                        this.silentProgress = true;

                        // 1) Normalize the manual+locked item (single-day lock)
                        const item: ApplyItem = {
                            resource_type: res.resource.resource_type,
                            resource_id: res.resource.resource_id,
                            date: res.date,
                            hours: Math.max(0, res.hours),
                            squares: Math.max(0, res.square_meters),
                            percent: undefined, // let backend compute percent
                            is_manual: true, // user-entered
                            is_locked: true, // lock this day/resource,
                            work_item_id: row.meta.workItemId,
                            work_type_id: row.meta.workTypeId,
                        };

                        const key = `${String(item.resource_type).toUpperCase()}:${Number(item.resource_id)}`;

                        // 2) Merge into pendingChanges (last write wins for same resource+date)
                        const nextPending = this.pendingChanges().filter(
                            (x) =>
                                !(
                                    String(x.resource_type).toUpperCase() ===
                                        String(
                                            item.resource_type
                                        ).toUpperCase() &&
                                    Number(x.resource_id) ===
                                        Number(item.resource_id) &&
                                    x.date === item.date
                                )
                        );

                        console.log('Item to add to pending: ', item);

                        // We do need to apply manual this to the db
                        try {
                            let response = await firstValueFrom(
                                this.capacityService.upsertWorkItemDayManual(
                                    row.meta.projectId,
                                    {
                                        ...item,
                                        // Pass through work item or work type so BE can create/link the item.
                                        work_item_id:
                                            row.meta?.workItemId &&
                                            row.meta.workItemId > 0
                                                ? row.meta.workItemId
                                                : null,
                                        work_type_id:
                                            row.meta?.workTypeId &&
                                            row.meta.workTypeId > 0
                                                ? row.meta.workTypeId
                                                : null,
                                        work_type_name:
                                            row.meta?.workTypeName ?? null,
                                    }
                                )
                            );

                            console.log('Response: ', response);

                            await this.load();

                            return;
                        } catch (e) {}
                    } catch (e) {
                        console.error('Case B weighted rebalance failed:', e);
                        // optionally surface toast/snackbar
                    } finally {
                        this.silentProgress = false;
                    }
                }
            });
    }

    /** razreže bar [start..end] okoli dneva d in vrne 1–3 nove segmente */
    splitBarByDate = (
        bar: any,
        d: string,
        inject: {
            allocation_percent?: number;
            total_hours?: number;
            is_manual: boolean;
            is_locked: boolean;
            resource_name?: string;
        }
    ) => {
        // const sd = bar.start_date;
        // const ed = bar.end_date;
        // const out: typeof bar[] = [];
        //
        // // levo [sd..d-1]
        // if (sd < d) out.push({...bar, end_date: this.prevDay(d)});
        //
        // // sredina [d..d]
        // out.push({
        //     ...bar,
        //     start_date: d,
        //     end_date: d,
        //     allocation_percent: inject.allocation_percent,
        //     total_hours: inject.total_hours,
        //     is_manual: inject.is_manual,
        //     is_locked: inject.is_locked,
        //     resource_name: inject.resource_name ?? bar.resource_name
        // });
        //
        // // desno [d+1..ed]
        // if (ed > d) out.push({...bar, start_date: this.nextDay(d)});
        //
        // return out;

        const sd = bar.start_date; // exclusive semantika: [sd, ed)
        const ed = bar.end_date;
        const out: (typeof bar)[] = [];

        // LEFT: [sd, d)
        if (sd < d) out.push({ ...bar, end_date: d }); // ⬅️ prej si imel prevDay(d)

        // MIDDLE: [d, d+1)
        out.push({
            ...bar,
            start_date: d,
            end_date: this.nextDay(d), // ⬅️ prej d..d
            allocation_percent: inject.allocation_percent,
            total_hours: inject.total_hours,
            is_manual: inject.is_manual,
            is_locked: inject.is_locked,
            resource_name: inject.resource_name ?? bar.resource_name,
        });

        // RIGHT: [d+1, ed)
        if (ed > this.nextDay(d))
            out.push({ ...bar, start_date: this.nextDay(d) });

        return out;
    };

    nextDay = (iso: string) => {
        const d = new Date(iso);
        d.setDate(d.getDate() + 1);
        return d.toISOString().slice(0, 10);
    };

    private openProjectDaySquareMetersDialog(
        projectId: number,
        workItemId: number,
        date: string,
        workTypeId?: number | null,
        workTypeName?: string | null
    ) {
        const safeWorkTypeId =
            typeof workTypeId === 'number' && workTypeId > 0
                ? workTypeId
                : null;
        // Read current value from index
        const total_square_meters = this.getProjectDaySquareMeters(
            projectId,
            workItemId,
            date
        );

        const ref = this.dialog.open(ItemComponent, {
            autoFocus: false,
            data: {
                content_type:
                    this.utils.ENTITY_TYPE_PROJECT_DAY_SQUARE_METERS_DIALOG,
                entity_type:
                    this.utils.ENTITY_TYPE_PROJECT_DAY_SQUARE_METERS_DIALOG,
                item: {
                    project_id: projectId,
                    work_item_id: workItemId ?? null,
                    work_type_id: safeWorkTypeId,
                    work_type_name: workTypeName ?? null,
                    date,
                    total_square_meters,
                },
            },
        });

        ref.afterClosed().subscribe((response: any) => {
            if (!response || response.cancelled) {
                return;
            }

            // Reload data (index will be rebuilt)
            this.load();
        });
    }

    private openProjectDayNotesDialog(
        projectId: number,
        workItemId: number | null,
        date: string
    ) {
        const current = this.getProjectDayNote(
            projectId,
            workItemId ?? 0,
            date
        );

        const ref = this.dialog.open(ItemComponent, {
            autoFocus: false,
            data: {
                content_type: this.utils.ENTITY_TYPE_PROJECT_DAY_NOTES,
                entity_type: this.utils.ENTITY_TYPE_PROJECT_DAY_NOTES,
                item: {
                    project_id: projectId,
                    work_item_id: workItemId ?? null,
                    date,
                    notes: current ?? '',
                },
            },
        });

        ref.afterClosed().subscribe((response: any) => {
            if (!response || response.cancelled) {
                return;
            }

            // Optimistically update local index so UI reflects change without full reload.
            const respNote =
                response?.notes ??
                response?.data?.notes ??
                response?.[1]?.notes ??
                '';
            this.indexProjectDayNotes.set(
                `${projectId}|${workItemId ?? 0}|${date}`,
                respNote
            );

            this.load();
        });
    }

    async applyTeamsChanges() {
        if (!this.dirtyTeamProjects.size) {
            return;
        }

        this.silentProgress = true;

        try {
            // Apply only projects that have been modified locally.
            for (const projectId of this.dirtyTeamProjects.values()) {
                const payload = this.buildTeamsPayloadForProject(projectId);
                if (!payload.length) continue;

                await firstValueFrom(
                    this.cap.applyProjectGroups(projectId, payload, true, false)
                );
            }

            this.dirtyTeamProjects.clear();
            this.teamsDirty = false;
        } catch (e) {
            console.error('Failed to apply vehicle changes:', e);
        } finally {
            this.silentProgress = false;
            this._changeDetectorRef.markForCheck();
        }
    }

    onTeamAction(e: {
        type: 'ADD_VEHICLE' | 'REMOVE_VEHICLE';
        date: string | Date;
        teamId: number | string;
        teamName: string;
        vehicle_id?: number;
        row?: Row;
    }) {
        const d =
            typeof e.date === 'string'
                ? e.date
                : e.date?.toISOString().slice(0, 10);

        if (!d) return;

        if (this.disablePastEditing && this.isPast(d)) {
            this.toastr.warning(
                this.transloco.translate('PAST_DATE_DISABLED_FOR_UTILIZATION')
            );
            return;
        }

        const projectId = e.row?.meta?.projectId;
        if (!projectId) {
            console.warn('Missing projectId for team action:', e);
            return;
        }

        if (e.type === 'ADD_VEHICLE') {
            // Use the shared vehicle picker dialog to select a vehicle.
            const ref = this.dialog.open(ItemComponent, {
                autoFocus: false,
                data: {
                    content_type: this.utils.ENTITY_TYPE_PROJECT_DAY_VEHICLE,
                    entity_type: this.utils.ENTITY_TYPE_PROJECT_DAY_VEHICLE,
                    item: {},
                },
            });

            ref.afterClosed().subscribe((response) => {
                if (!response || response.cancelled) {
                    return;
                }

                const picked: any = response[1];
                if (!picked) return;

                this.onVehiclePicked(projectId, d, {
                    id: picked.vehicle_id,
                    name: picked.name,
                    plate: picked.reg_num,
                    is_locked: picked.is_locked || false,
                    notes: picked.notes || '',
                });
            });
        } else if (e.type === 'REMOVE_VEHICLE' && e.vehicle_id != null) {
            this.removeVehicleFromDay(projectId, d, e.vehicle_id);
        }
    }

    private onVehiclePicked(
        projectId: number,
        date: string | Date,
        picked: VehicleChip
    ) {
        let key: string;

        if (typeof date === 'string') {
            key = date.slice(0, 10);
        } else if (date instanceof Date) {
            key = date.toISOString().slice(0, 10);
        } else {
            console.warn('onVehiclePicked(): invalid date', date);
            return;
        }

        if (!picked || picked.id == null) return;

        const currMap = this.projectTeamsByDate.get(projectId) ?? {};

        const ensuredTeam = this.ensureProjectDayTeam(projectId, key);
        const teamsForDay = (this.projectTeamsByDate.get(projectId)?.[key] ??
            []) as Array<any>;

        const idx = teamsForDay.findIndex(
            (t) => t.teamId === ensuredTeam.teamId
        );

        const existingVehicles =
            idx >= 0 && Array.isArray(teamsForDay[idx].vehicles)
                ? teamsForDay[idx].vehicles
                : Array.isArray(ensuredTeam.vehicles)
                  ? ensuredTeam.vehicles
                  : [];

        // Avoid duplicates for the same day/team.
        if (existingVehicles.some((v) => v.id === picked.id)) {
            return;
        }

        const baseTeam = idx >= 0 ? teamsForDay[idx] : ensuredTeam;
        const updatedTeam = {
            ...baseTeam,
            vehicles: [...existingVehicles, picked],
            dirty: true,
        };

        const updatedTeamsForDay =
            idx === -1
                ? [...teamsForDay, updatedTeam]
                : [
                      ...teamsForDay.slice(0, idx),
                      updatedTeam,
                      ...teamsForDay.slice(idx + 1),
                  ];

        this.projectTeamsByDate.set(projectId, {
            ...currMap,
            [key]: updatedTeamsForDay,
        });

        // Optimistically refresh timeline inputs.
        this.rows.set([...this.rows()]);
        this.bumpTeamsResolver();

        // Persist immediately so user sees the result without page reload.
        this.markTeamsDirty(projectId);
        this.persistProjectVehicles(projectId, [key]);
        this._changeDetectorRef.markForCheck();
        this._changeDetectorRef.detectChanges();
    }

    private removeVehicleFromDay(
        projectId: number,
        date: string,
        vehicle_id: number
    ) {
        const byDate = this.projectTeamsByDate.get(projectId) ?? {};
        const arr = byDate[date] ?? [];
        const idx = arr.findIndex((t) => t.kind === 'PROJECT_DAY');
        if (idx >= 0) {
            const unit = {
                ...arr[idx],
                dirty: true,
                vehicles: arr[idx].vehicles.filter((v) => v.id !== vehicle_id),
            };
            const next = arr.slice();
            next[idx] = unit;

            this.projectTeamsByDate.set(projectId, {
                ...byDate,
                [date]: next,
            });
            // Optimistic UI refresh for removal.
            this.rows.set([...this.rows()]);
            this.bumpTeamsResolver();
            // Persist immediately so the UI stays in sync without a full reload.
            this.markTeamsDirty(projectId);
            this.persistProjectVehicles(projectId, [date]);
            this._changeDetectorRef.markForCheck();
            this._changeDetectorRef.detectChanges();
        }
    }

    /** Handle action from team vehicle chip */
    handleAction(e: {
        type: RowAction['id'];
        date?: string | Date;
        projectId?: number;
        workItemId?: number;
        row?: Row;
        action?: RowAction;
    }) {
        console.log('Handling action:', e);
        const d =
            typeof e.date === 'string'
                ? e.date
                : e.date?.toISOString().slice(0, 10) ?? null;

        if (this.disablePastEditing && this.isPast(d)) {
            this.toastr.warning(
                this.transloco.translate('PAST_DATE_DISABLED_FOR_UTILIZATION')
            );
            return;
        }

        if (e.type === 'ADD_RESOURCE') {
            // Build exclude list using shared parser (USER / EXT_CONTRACTOR only)
            const exclude: ResourceRef[] = [];
            const seen = new Set<string>();

            for (const r of this.rows() ?? []) {
                // Only rows belonging to this project
                if (r.meta?.projectId !== e.projectId) continue;

                // Delegate parsing to CapacityService
                const parsed = this.capacityService.parseResourceFromRow(r);
                if (!parsed) continue;

                const key = `${parsed.resource_type}:${parsed.resource_id}`;
                if (seen.has(key)) continue;
                seen.add(key);

                exclude.push({
                    resource_type: parsed.resource_type,
                    resource_id: parsed.resource_id,
                    resource_name: r.resourceName,
                });
            }

            // We should open up a new dialog to fill up the details of the new content item
            let ref = this.dialog.open(AddResourceDialogComponent, {
                autoFocus: false,
                width: '520px',
                data: {
                    entity_type: this.utils.ENTITY_TYPE_ADD_PROJECT_RESOURCE,
                    projectId: e.projectId,
                    mode: 'global',
                    exclude,
                    planningPermissions: this.planningPermissions,
                },
            });

            ref.afterClosed().subscribe((response) => {
                if (!response) {
                    // no changes
                    // this.changes = false;
                    return;
                }
                if (response.cancelled) {
                    return;
                }

                console.log('Response from AddResourceDialog:', response);
                const rows = [...this.rows()];
                const projectId = e.projectId;

                // 1) Find PROJECT row
                const projectIndex = rows.findIndex(
                    (r) =>
                        r.kind === 'PROJECT' && r.meta?.projectId === projectId
                );

                if (projectIndex === -1) {
                    console.warn(
                        'PROJECT row not found for project',
                        projectId
                    );
                    return;
                }

                // 2) Collect all work items under this project (in order)
                const workItems: { index: number; workItemId: number }[] = [];
                for (let i = projectIndex + 1; i < rows.length; i++) {
                    const r = rows[i];
                    if (r.kind === 'PROJECT') break; // next project block
                    if (
                        r.kind === 'WORK_ITEM' &&
                        r.meta?.projectId === projectId
                    ) {
                        const id = r.meta?.workItemId;
                        if (id != null) {
                            workItems.push({ index: i, workItemId: id });
                        }
                    }
                }

                // 3) If no work items exist, attach to project-level (fallback)
                if (!workItems.length) {
                    rows.splice(projectIndex + 1, 0, {
                        resourceKey: `PROJECT:${projectId}|${response.resource.resource_type}:${response.resource.resource_id}`,
                        resourceName: response.resource.resource_name,
                        kind: 'EMPLOYEE',
                        class: '!pl-4 italic',
                        meta: { projectId },
                        bars: [],
                    });
                } else {
                    // Insert under every work item, before its specials
                    for (const wi of workItems) {
                        // re-evaluate indices as rows grow
                        const wiPos = rows.findIndex(
                            (r, idx) =>
                                idx >= projectIndex &&
                                r.kind === 'WORK_ITEM' &&
                                r.meta?.projectId === projectId &&
                                r.meta?.workItemId === wi.workItemId
                        );

                        let insertIndex = wiPos + 1;
                        for (let i = wiPos + 1; i < rows.length; i++) {
                            const r = rows[i];
                            // stop at next work item or project or global special
                            if (
                                r.kind === 'PROJECT' ||
                                (r.kind === 'WORK_ITEM' &&
                                    r.meta?.projectId === projectId &&
                                    r.meta?.workItemId !== wi.workItemId) ||
                                r.resourceKey === 'SPECIAL:UNASSIGNED_EMPLOYEES'
                            ) {
                                break;
                            }
                            if (
                                r.meta?.projectId === projectId &&
                                r.meta?.workItemId === wi.workItemId
                            ) {
                                if (r.kind === 'SPECIAL') break; // don't pass specials
                                insertIndex = i + 1;
                            }
                        }

                        rows.splice(insertIndex, 0, {
                            resourceKey: `PROJECT:${projectId}|WORK_ITEM:${wi.workItemId}|${response.resource.resource_type}:${response.resource.resource_id}`,
                            resourceName: response.resource.resource_name,
                            kind: 'EMPLOYEE',
                            class: '!pl-8 italic',
                            meta: { projectId, workItemId: wi.workItemId },
                            bars: [],
                        });
                    }
                }

                this.rows.set(rows);

                console.log('Updated rows:', this.rows());
            });
        } else if (e.type === 'ADD_WORK_TYPE') {
            // Build exclude list so already-added work types for this project cannot be picked again.
            const excludeWorkTypeIds = Array.from(
                new Set(
                    (this.rows() ?? [])
                        .filter(
                            (r) =>
                                r.kind === 'WORK_ITEM' &&
                                r.meta?.projectId === e.projectId &&
                                r.meta?.workTypeId
                        )
                        .map((r) => Number(r.meta?.workTypeId))
                        .filter((id) => !Number.isNaN(id))
                )
            );

            const ref = this.dialog.open(ItemComponent, {
                autoFocus: false,
                data: {
                    content_type: this.utils.ENTITY_TYPE_PROJECT_WORK_ITEM,
                    entity_type: this.utils.ENTITY_TYPE_PROJECT_WORK_ITEM,
                    item: {
                        project_id: e.projectId,
                    },
                    additional: {
                        excludeWorkTypeIds,
                    },
                },
            });

            ref.afterClosed().subscribe((response: any) => {
                if (!response || response.cancelled) {
                    return;
                }

                console.log('Response from ItemComponent:', response);

                // Insert new WORK_ITEM row under the correct PROJECT row and before employees/specials
                const rows = [...this.rows()];
                const projectId = e.projectId;
                const projectKey = `PROJECT:${projectId}`;
                let workType: WorkType = response;
                const workTypeId = Number(workType.id);
                const workTypeName = workType.name ?? null;
                // Use a temporary negative work item id; backend will generate the real id on first save.
                const workItemId = -Date.now();

                // 1) Find PROJECT row
                const projectIndex = rows.findIndex(
                    (r) =>
                        r.kind === 'PROJECT' && r.meta?.projectId === projectId
                );

                if (projectIndex === -1) {
                    console.warn(
                        'PROJECT row not found for project',
                        projectId
                    );
                    return;
                }

                // 2) Find insertion index: place after the last SPECIAL row belonging to this project
                // (so PROJECT_DAY_M2 and PROJECT_DAY_NOTES stay before new items).
                let insertIndex = projectIndex + 1;
                let lastSpecialIndex = -1;
                let lastProjectRowIndex = projectIndex;

                for (let i = projectIndex + 1; i < rows.length; i++) {
                    const r = rows[i];

                    // Stop scanning when we leave this project block or hit global specials.
                    if (
                        r.kind === 'PROJECT' ||
                        r.resourceKey === 'SPECIAL:UNASSIGNED_EMPLOYEES'
                    ) {
                        break;
                    }

                    // Track the furthest row within this project block
                    if (r.meta?.projectId === projectId) {
                        lastProjectRowIndex = i;
                    }

                    if (r.meta?.projectId === projectId) {
                        const isProjectSpecial =
                            r.kind === 'SPECIAL' &&
                            (r.meta?.type === 'PROJECT_DAY_M2' ||
                                r.meta?.type === 'PROJECT_DAY_NOTES');
                        if (isProjectSpecial) {
                            lastSpecialIndex = i;
                        }
                    }
                }

                insertIndex =
                    lastSpecialIndex >= 0
                        ? lastSpecialIndex + 1
                        : lastProjectRowIndex + 1;

                // Collect unique employees already present in this project so we can mirror them.
                const employeesForProject: Array<{
                    resource_type: string;
                    resource_id: number;
                    resourceName: string;
                }> = [];
                const seenEmp = new Set<string>();

                for (const r of rows) {
                    if (
                        r.kind === 'EMPLOYEE' &&
                        r.meta?.projectId === projectId
                    ) {
                        const parsed =
                            this.capacityService.parseResourceFromRow(r);
                        if (!parsed) continue;
                        const k = `${parsed.resource_type}:${parsed.resource_id}`;
                        if (seenEmp.has(k)) continue;
                        seenEmp.add(k);
                        employeesForProject.push({
                            resource_type: parsed.resource_type,
                            resource_id: parsed.resource_id,
                            resourceName: r.resourceName,
                        });
                    }
                }

                // 3) Insert new WORK ITEM row
                rows.splice(insertIndex, 0, {
                    resourceKey: `${projectKey}|WORK_ITEM:${workItemId}`,
                    resourceName: workType.name ?? '',
                    kind: 'WORK_ITEM',
                    class: `${this.workTypeBg(workTypeId, '50')} !pl-4 font-semibold`,
                    meta: { projectId, workItemId, workTypeId, workTypeName },
                    bars: [],
                });

                // 4) Clone employee rows for this project under the new work item (empty bars).
                let cursor = insertIndex + 1;
                for (const emp of employeesForProject) {
                    rows.splice(cursor, 0, {
                        resourceKey: `${projectKey}|WORK_ITEM:${workItemId}|${emp.resource_type}:${emp.resource_id}`,
                        resourceName: emp.resourceName,
                        kind: 'EMPLOYEE',
                        class: '!pl-8 italic',
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            workTypeName,
                        },
                        bars: [],
                    });
                    cursor++;
                }

                // 5) Also insert PROJECT_DAY_M2 special row directly after cloned employees
                if (!this.filterObject.employee_ids?.length) {
                    rows.splice(cursor, 0, {
                        resourceKey: `${projectKey}|WORK_ITEM:${workItemId}|PROJECT_DAY_M2`,
                        resourceName: 'm² / day',
                        kind: 'SPECIAL',
                        class: `!pl-8 ${this.workTypeBg(workTypeId, '100')} font-semibold`,
                        meta: {
                            projectId,
                            workItemId,
                            workTypeId,
                            workTypeName,
                            type: 'PROJECT_DAY_M2',
                        },
                        bars: this.buildProjectDayM2Bars(
                            projectId,
                            workItemId,
                            this.from ?? this.dates()[0],
                            this.to ?? this.dates()[this.dates().length - 1]
                        ),
                    });
                }

                // 6) Insert PROJECT DAY NOTES special row
                rows.splice(cursor + 1, 0, {
                    resourceKey: `${projectKey}|WORK_ITEM:${workItemId}|PROJECT_DAY_NOTES`,
                    resourceName: this.transloco.translate('NOTES'),
                    kind: 'SPECIAL',
                    class: `!pl-8 ${this.workTypeBg(workTypeId, '200')} font-semibold`,
                    meta: {
                        projectId,
                        workItemId,
                        workTypeId,
                        workTypeName,
                        type: 'PROJECT_DAY_NOTES',
                    },
                    bars: this.buildProjectDayNotesBars(
                        projectId,
                        workItemId,
                        this.from ?? this.dates()[0],
                        this.to ?? this.dates()[this.dates().length - 1]
                    ),
                });

                this.rows.set(rows);

                console.log('Updated rows:', this.rows());
            });
        }
    }

    private readonly workTypeBgPalette: Array<
        Record<'50' | '100' | '200', string>
    > = [
        {
            '50': 'bg-indigo-50',
            '100': 'bg-indigo-100',
            '200': 'bg-indigo-200',
        },
        {
            '50': 'bg-emerald-50',
            '100': 'bg-emerald-100',
            '200': 'bg-emerald-200',
        },
        { '50': 'bg-amber-50', '100': 'bg-amber-100', '200': 'bg-amber-200' },
        { '50': 'bg-sky-50', '100': 'bg-sky-100', '200': 'bg-sky-200' },
        { '50': 'bg-rose-50', '100': 'bg-rose-100', '200': 'bg-rose-200' },
        {
            '50': 'bg-purple-50',
            '100': 'bg-purple-100',
            '200': 'bg-purple-200',
        },
    ];

    /** Deterministic soft background per work type id; higher shade → stronger tint. */
    workTypeBg(workTypeId: number, shade: '50' | '100' | '200' = '50'): string {
        const palette = this.workTypeBgPalette;
        const pick =
            palette[Math.abs(workTypeId) % palette.length] ?? palette[0];
        return pick[shade];
    }

    /**
     * Union of all resources that appear on any work item of the project (within loaded window).
     */
    private collectProjectWorkItemResources(
        projectId: number
    ): Map<
        string,
        { resourceName: string; resource_type: string; resource_id: number }
    > {
        const pool = new Map<
            string,
            { resourceName: string; resource_type: string; resource_id: number }
        >();

        for (const [key, arr] of this.indexByProjectWorkItem.entries()) {
            if (!key.startsWith(`${projectId}|`)) continue;
            for (const r of arr) {
                if (!this.isValidResourceRef(r)) continue;
                const k = `${r.resource_type}:${r.resource_id}`;
                if (pool.has(k)) continue;
                const resource_id = Number(r.resource_id);
                const u = this.users.find((x) => Number(x.id) === resource_id);
                const resourceName = this.formatResourceName({
                    first_name: u?.first_name,
                    last_name: u?.last_name,
                    resource_name: r.resource_name,
                    resource_type: r.resource_type,
                    resource_id,
                });
                pool.set(k, {
                    resourceName,
                    resource_type: r.resource_type,
                    resource_id,
                });
            }
        }

        return pool;
    }

    /**
     * Best-effort formatter that avoids "null null" and empty strings.
     */
    private formatResourceName(data: {
        first_name?: string | null;
        last_name?: string | null;
        resource_name?: string | null;
        resource_type?: string | null;
        resource_id?: number | string | null;
    }): string {
        const clean = (v?: string | null) =>
            v && v !== 'null' && v !== 'undefined' ? v : '';
        const parts = [clean(data.first_name), clean(data.last_name)].filter(
            Boolean
        );
        const full = parts.join(' ').trim();
        if (full) return full;

        const fallback = clean(data.resource_name);
        if (fallback) return fallback;

        const typePart = clean(data.resource_type);
        const idPart =
            data.resource_id !== undefined && data.resource_id !== null
                ? String(data.resource_id)
                : '';
        const joined = [typePart || 'RESOURCE', idPart]
            .filter(Boolean)
            .join(' ');
        return joined || 'Unknown';
    }

    /** Basic validation for resource refs coming from backend rows. */
    private isValidResourceRef(r: {
        resource_type?: string | null;
        resource_id?: number | string | null;
    }): boolean {
        if (!r) return false;
        const typeOk =
            r.resource_type !== null &&
            r.resource_type !== undefined &&
            r.resource_type !== 'null' &&
            r.resource_type !== '';
        const id =
            r.resource_id !== null && r.resource_id !== undefined
                ? Number(r.resource_id)
                : NaN;
        const idOk = Number.isFinite(id) && id > 0;
        return typeOk && idOk;
    }
}
